<template>
    <div class="notification-settings" :class="{ 
        'sending-settings-mode': displayMode === 'sending-settings',
        'notifications-mode': displayMode === 'notifications',
        'mailings-mode': displayMode === 'mailings'
    }">
        <div v-if="hasPendingDeauthChanges && !loading && !hideSaveButton" class="unsaved-changes-popup">
            <span>Были внесены изменения.</span>
            <v-btn
              small
              outlined
              class="unsaved-changes-popup__save-btn"
              :loading="saveButtonLoading"
              :disabled="!isValid || saveButtonLoading"
              @click="save"
            >
                СОХРАНИТЬ
            </v-btn>
        </div>
        <!-- Режим "настройки отправки" + унифицированный UI для "настройки рассылок" (mailings) -->
        <div
            v-if="!loading && (displayMode === 'sending-settings' || displayMode === 'mailings')"
            class="sending-settings-container"
        >
            <v-expansion-panels flat class="settings-panels" v-model="sendingStepsIndexes" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-header class="panel-header">
                        <div class="panel-header-content">
                            <div class="d-flex align-center">
                                <div class="panel-section-title">Общие настройки отправки</div>
                            </div>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <CommonSettingsBlock
                          :settings="settings"
                          :updateSetting="updateSetting"
                          :phoneValidator="phoneValidator"
                          :isValidPhone="isValidPhone"
                          :get="get"
                          :showPhoneForNotification="hasBotConnector"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="panel-header">
                        <div class="panel-header-content">
                            <div class="d-flex align-center">
                                <div class="panel-section-title panel-section-title--deauth">
                                    <span class="panel-section-title__desktop">Контакты в Telegram, куда будут приходить уведомления о потере авторизации каналов</span>
                                    <span class="panel-section-title__mobile">Уведомления о потере авторизации</span>
                                </div>
                            </div>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <DeauthNotificationRecipientsBlock
                          :phoneValidator="phoneValidator"
                          :deauthContactsValue="deauthContactsValue"
                          :deauthContacts="deauthContacts"
                          :deauthAllGroups="deauthAllGroups"
                          :accessErrorMessage="deauthAccessErrorMessage"
                          :canManageDeauthRecipients="canManageDeauthRecipients"
                          :isSuperUser="isSuperUser"
                          :updateDeauthContactsValue="updateDeauthContactsValue"
                          :removeDeauthContact="removeDeauthContact"
                          :verifyDeauthContact="verifyDeauthContact"
                          :deauthVerifyingIndices="deauthVerifyingIndices"
                          :sendToReportRecipients="sendToReportRecipients"
                          :updateSendToReportRecipients="updateSendToReportRecipients"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Настройки бота -->
                <v-expansion-panel v-if="displayMode === 'sending-settings' && hasBotConnector">
                    <v-expansion-panel-header class="panel-header">
                        <div class="panel-header-content">
                            <div class="d-flex align-center">
                                <div class="panel-section-title">Настройки сервисных уведомлений</div>
                            </div>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <ServiceNotificationsBlock
                          :settings="settings"
                          :updateSetting="updateSetting"
                          :setTimezone="setTimezone"
                          :phoneValidator="phoneValidator"
                          :isValidPhone="isValidPhone"
                          :get="get"
                          :modeHintText="modeHintText"
                          :redirectPhoneHintText="redirectPhoneHintText"
                          :visitGroupHintText="visitGroupHintText"
                          :hasYClientsConnector="Boolean(yClientsConnector)"
                          :hasBotCrmConnector="Boolean(botCrmConnector)"
                          :channelOptions="channelOptions"
                          :selectedChannel="selectedChannel"
                          :updateChannel="updateChannel"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Настройки рассылок -->
                <v-expansion-panel>
                    <v-expansion-panel-header class="panel-header">
                        <div class="panel-header-content">
                            <div class="d-flex align-center">
                                <div class="panel-section-title">Настройки маркетинговых рассылок</div>
                            </div>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <MarketingMailingsBlock
                          :settings="settings"
                          :updateSetting="updateSetting"
                          :items="mailingsChannelOptions"
                          :selectedValue="selectedActivationSendChannel"
                          :updateValue="updateActivationSendChannel"
                          :selectedExists="Boolean(effectiveActivationSendChannel)"
                          :showDailyLimit="hasMessagingConnector"
                          :showDefaultChannel="isMailingsCascadeEnabled"
                          :extraVarFields="extraVarFields"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Настройки отправки сообщений из YCLIENTS -->
                <v-expansion-panel v-if="displayMode === 'sending-settings' && hasBotConnector && yClientsConnector">
                    <v-expansion-panel-header class="panel-header">
                        <div class="panel-header-content">
                            <div class="d-flex align-center">
                                <div class="panel-section-title">Настройки отправки сообщений из YCLIENTS (коды
                                    подтверждения)
                                </div>
                            </div>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <YclientsMessagesBlock
                          :settings="settings"
                          :yclientsChannelOptions="yclientsChannelOptions"
                          :selectedYclientsChannel="selectedYclientsChannel"
                          :updateYclientsChannel="updateYclientsChannel"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <div v-if="!hideSaveButton" class="save-button-container">
                <v-tooltip bottom max-width="300px" :disabled="isValid">
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" :style="{ display: 'inline-block' }">
                            <v-btn
                              elevation="0"
                              class="btn-save"
                              @click="save"
                              :loading="saveButtonLoading"
                              :disabled="!isValid"
                            >{{ saveButtonText }}</v-btn>
                        </span>
                    </template>
                    <span class="white--text">Заполните все обязательные поля</span>
                </v-tooltip>
            </div>
        </div>

        <!-- Режим "настройки уведомлений" и "настройки рассылок" (первая и вторая колонки) -->
        <div
          v-if="!loading && (displayMode === 'default' || displayMode === 'notifications')"
          class="whatsapp-settings-form">
            <div class="left-panel">
                <v-expansion-panels flat class="settings-panels" v-model="stepsIndexes" multiple>
                    <v-expansion-panel>
                        <v-expansion-panel-header class="panel-header">
                            <div class="panel-header-content">
                                <div class="d-flex align-center">
                                    <div v-if="displayMode === 'notifications'" class="panel-section-title">Настройки сервисных уведомлений</div>
                                    <div v-else-if="displayMode === 'mailings'" class="panel-section-title">Настройки маркетинговых рассылок</div>
                                    <div v-else class="panel-section-title">Выберите режим работы бота</div>
                                </div>
                                <div v-if="!mainSettingsValid" class="d-flex align-center">
                                    <FieldStatusIcon :ok="false" />
                                    <span class="ml-1 header-warning-text">Есть обязательные поля для заполнения</span>
                                </div>
                                <div v-else-if="mainSettingsValid" class="d-flex align-center">
                                    <FieldStatusIcon :ok="true" />
                                    <span class="ml-1 header-warning-text">Все обязательные поля заполнены</span>
                                </div>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="panel-content">
                                <div v-if="displayMode !== 'mailings'">
                                    <div class="d-flex align-center">
                                        <div class="subtitle">Режим работы (рекомендуем изначально подключить тестовый
                                            режим)
                                        </div>
                                        <SmallHint class="ml-1" :text="modeHintText" :size="16"></SmallHint>
                                    </div>
                                    <v-radio-group
                                      v-model="redirectMode"
                                      row
                                      class="mt-0"
                                    >
                                        <v-radio
                                          label="Тестовый"
                                          :value="true"
                                          color="mainNew"
                                        ></v-radio>
                                        <v-radio
                                          label="Боевой"
                                          :value="false"
                                          color="mainNew"
                                        ></v-radio>
                                    </v-radio-group>
                                </div>

                                <div v-if="get(settings, 'redirect.needToRedirect') && displayMode !== 'mailings'">
                                    <div class="d-flex align-center field-label">
                                        <FieldStatusIcon :ok="isValidPhone(get(settings, 'redirect.redirectPhone'))" />
                                        <div class="subtitle">Номер телефона, на который будут отправляться сервисные
                                            сообщения
                                        </div>
                                        <SmallHint class="ml-1" :text="redirectPhoneHintText"
                                                   :size="16"></SmallHint>
                                    </div>
                                    <ValidationInput
                                      :rule="phoneValidator"
                                      label="+7 (999) 123-45-67"
                                      :value="get(settings, 'redirect.redirectPhone')"
                                      @input="updateSetting('redirect.redirectPhone', $event)"
                                      class="phone-input"
                                    />
                                </div>

                                <div>
                                    <div class="d-flex align-center field-label">
                                        <FieldStatusIcon :ok="Boolean(settings['timezone'])" />
                                        <div class="subtitle">Часовой пояс</div>
                                    </div>
                                    <TimeZonePicker
                                      :value="settings['timezone']"
                                      :recommendCurrentTimezone="true"
                                      @input="setTimezone($event)"
                                      label="Выбрать"
                                      class="timezone-picker"
                                      append-icon="mdi-menu-down"
                                      :persistent-label="false"
                                      hide-details
                                    />
                                </div>

                                <div v-if="displayMode === 'notifications'">
                                    <div class="d-flex align-center field-label">
                                        <FieldStatusIcon :ok="Boolean(settings['defaultChannel'])" />
                                        <div class="subtitle">Канал отправки уведомлений (по умолчанию)</div>
                                    </div>
                                    <CustomActivatorSelect
                                      :value="selectedChannel"
                                      @input="updateChannel"
                                      :items="channelOptions"
                                      item-value="value"
                                      item-name="label"
                                      :multiple="false"
                                      class="custom-input"
                                    >
                                        <template v-slot:activator="{ selected, _id }">
                                            <BaseInputOutlined
                                              :id="_id"
                                              :value="selected ? selected.label : ''"
                                              label="Выберите канал"
                                              readonly
                                              class="select-input"
                                              append-icon="mdi-menu-down"
                                              hide-details
                                            />
                                        </template>
                                    </CustomActivatorSelect>
                                </div>

                                <!-- Для режима рассылок добавляем поля из второй панели -->
                                <template v-if="displayMode === 'mailings'">
                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="Boolean(settings['salonName'] && settings['salonName'].trim().length > 0)" />
                                            <div class="subtitle">Название салона в подписи к сообщениям</div>
                                        </div>
                                        <BaseInputOutlined
                                          label="Введите название"
                                          :value="settings['salonName']"
                                          @input="updateSetting('salonName', $event)"
                                          class="custom-input"
                                          hide-details
                                        />
                                    </div>

                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="Boolean(settings['salonAddress'] && settings['salonAddress'].trim().length > 0)" />
                                            <div class="subtitle">Адрес салона в подписи к сообщениям</div>
                                        </div>
                                        <BaseInputOutlined
                                          label="Введите адрес"
                                          :value="settings['salonAddress']"
                                          @input="updateSetting('salonAddress', $event)"
                                          class="custom-input"
                                          hide-details
                                        />
                                    </div>

                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="isValidPhone(get(settings, 'salonPhone'))" />
                                            <div class="subtitle">Контактный телефон в подписи к сообщениям</div>
                                        </div>
                                        <ValidationInput
                                          :rule="phoneValidator"
                                          label="+7 (999) 123-45-67"
                                          :value="settings['salonPhone']"
                                          @input="updateSetting('salonPhone', $event)"
                                          class="phone-input"
                                        />
                                    </div>

                                    <!-- Канал отправки для режима рассылок -->
                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="Boolean(effectiveActivationSendChannel)" />
                                            <div class="subtitle">Канал отправки маркетинговых рассылок (по умолчанию)
                                            </div>
                                        </div>
                                        <CustomActivatorSelect
                                          :value="selectedActivationSendChannel"
                                          @input="updateActivationSendChannel"
                                          :items="mailingsChannelOptions"
                                          item-value="value"
                                          item-name="label"
                                          :multiple="false"
                                          class="custom-input"
                                        >
                                            <template v-slot:activator="{ selected, _id }">
                                                <BaseInputOutlined
                                                  :id="_id"
                                                  :value="selected ? selected.label : ''"
                                                  label="Выберите канал"
                                                  readonly
                                                  class="select-input"
                                                  append-icon="mdi-menu-down"
                                                />
                                            </template>
                                        </CustomActivatorSelect>
                                    </div>

                                    <!-- Переменные для подстановки -->
                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="Boolean(settings['eventDate'])" />
                                            <div class="subtitle">Дата-мероприятия</div>
                                            <span class="ml-2 optional-text">*необязательное</span>
                                        </div>
                                        <BaseInputOutlined
                                          label="Введите значение"
                                          :value="settings['eventDate']"
                                          @input="updateSetting('eventDate', $event)"
                                          class="custom-input"
                                          hide-details
                                        />
                                    </div>

                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="Boolean(settings['reviewLink'])" />
                                            <div class="subtitle">Ссылка-на-отзывы</div>
                                            <span class="ml-2 optional-text">*необязательное</span>
                                        </div>
                                        <BaseInputOutlined
                                          label="Введите значение"
                                          :value="settings['reviewLink']"
                                          @input="updateSetting('reviewLink', $event)"
                                          class="custom-input"
                                          hide-details
                                        />
                                    </div>

                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="Boolean(settings['giftName'])" />
                                            <div class="subtitle">Название-подарка</div>
                                            <span class="ml-2 optional-text">*необязательное</span>
                                        </div>
                                        <BaseInputOutlined
                                          label="Введите значение"
                                          :value="settings['giftName']"
                                          @input="updateSetting('giftName', $event)"
                                          class="custom-input"
                                          hide-details
                                        />
                                    </div>

                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <FieldStatusIcon :ok="Boolean(settings['bookingLink'])" />
                                            <div class="subtitle">Ссылка-на-онлайн-запись</div>
                                            <span class="ml-2 optional-text">*необязательное</span>
                                        </div>
                                        <BaseInputOutlined
                                          label="Введите значение"
                                          :value="settings['bookingLink']"
                                          @input="updateSetting('bookingLink', $event)"
                                          class="custom-input"
                                          hide-details
                                        />
                                    </div>

                                    <!-- Дневной лимит -->
                                    <div>
                                        <div class="d-flex align-center field-label">
                                            <div class="subtitle">Лимит сообщений в день для пакетной отправки</div>
                                        </div>
                                        <BaseInputOutlined
                                          label="Введите значение"
                                          :value="settings['dailyLimit']"
                                          @input="updateSetting('dailyLimit', $event)"
                                          class="custom-input"
                                          type="number"
                                          hide-details
                                        />
                                        <div class="warning-box">
                                            <strong>Внимание!</strong> Данные лимиты применяются для пакетной рассылки
                                            по всем активациям. Установленный лимит работает для всех активаций
                                            единовременно
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Вторая панель только для режима уведомлений -->
                    <v-expansion-panel v-if="displayMode !== 'mailings'">
                        <v-expansion-panel-header class="panel-header">
                            <div class="panel-header-content">
                                <div class="d-flex align-center">
                                    <div class="d-flex align-center">
                                        <div v-if="displayMode === 'notifications'" class="panel-section-title">Общие настройки</div>
                                        <div v-else class="panel-section-title">Заполните необходимую информацию</div>
                                    </div>
                                    <SmallHint v-if="displayMode !== 'notifications'" class="ml-1"
                                        :text="informationSettingsHintText" :size="16">
                                    </SmallHint>
                                </div>
                                <div v-if="!informationSettingsValid" class="d-flex align-center">
                                    <FieldStatusIcon :ok="false" />
                                    <span class="ml-1 header-warning-text">Есть обязательные поля для заполнения</span>
                                </div>
                                <div v-else-if="informationSettingsValid" class="d-flex align-center">
                                    <FieldStatusIcon :ok="true" />
                                    <span class="ml-1 header-warning-text">Все обязательные поля заполнены</span>
                                </div>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="panel-content">
                                <div>
                                    <div class="d-flex align-center field-label">
                                        <FieldStatusIcon :ok="Boolean(settings['salonName'] && settings['salonName'].trim().length > 0)" />
                                        <div class="subtitle">Название салона в подписи к сообщениям</div>
                                    </div>
                                    <BaseInputOutlined
                                      label="Введите название"
                                      :value="settings['salonName']"
                                      @input="updateSetting('salonName', $event)"
                                      class="custom-input"
                                      hide-details
                                    />
                                </div>

                                <div>
                                    <div class="d-flex align-center field-label">
                                        <FieldStatusIcon :ok="Boolean(settings['salonAddress'] && settings['salonAddress'].trim().length > 0)" />
                                        <div class="subtitle">Адрес салона в подписи к сообщениям</div>
                                    </div>
                                    <BaseInputOutlined
                                      label="Введите адрес"
                                      :value="settings['salonAddress']"
                                      @input="updateSetting('salonAddress', $event)"
                                      class="custom-input"
                                      hide-details
                                    />
                                </div>

                                <div>
                                    <div class="d-flex align-center field-label">
                                        <FieldStatusIcon :ok="isValidPhone(get(settings, 'salonPhone'))" />
                                        <div class="subtitle">Контактный телефон в подписи к сообщениям</div>
                                    </div>
                                    <ValidationInput
                                      :rule="phoneValidator"
                                      label="+7 (999) 123-45-67"
                                      :value="settings['salonPhone']"
                                      @input="updateSetting('salonPhone', $event)"
                                      class="phone-input"
                                    />
                                </div>

                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel v-if="displayMode !== 'mailings'">
                        <v-expansion-panel-header class="panel-header">
                            <div class="panel-header-content">
                                <div class="d-flex align-center">
                                    <div class="panel-section-title panel-section-title--deauth">
                                        <span class="panel-section-title__desktop">Контакты в Telegram, куда будут приходить уведомления о потере авторизации каналов</span>
                                        <span class="panel-section-title__mobile">Уведомления о потере авторизации</span>
                                    </div>
                                </div>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="panel-content">
                                <DeauthNotificationRecipientsBlock
                                  :phoneValidator="phoneValidator"
                                  :deauthContactsValue="deauthContactsValue"
                                  :deauthContacts="deauthContacts"
                                  :deauthAllGroups="deauthAllGroups"
                                  :accessErrorMessage="deauthAccessErrorMessage"
                                  :canManageDeauthRecipients="canManageDeauthRecipients"
                                  :isSuperUser="isSuperUser"
                                  :updateDeauthContactsValue="updateDeauthContactsValue"
                                  :removeDeauthContact="removeDeauthContact"
                                  :verifyDeauthContact="verifyDeauthContact"
                                  :deauthVerifyingIndices="deauthVerifyingIndices"
                                  :sendToReportRecipients="sendToReportRecipients"
                                  :updateSendToReportRecipients="updateSendToReportRecipients"
                                />
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Дополнительные настройки (только для режима уведомлений) -->
                    <v-expansion-panel v-if="settings.phone && displayMode !== 'mailings'">
                        <v-expansion-panel-header class="panel-header">
                            <div class="panel-header-content">
                                <div class="d-flex align-center">
                                    <div class="panel-section-title">Подключите доп.функции</div>
                                    <span
                                      class="header-warning-text ml-1">(Необязательно, можно сделать позже)</span>
                                </div>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="panel-content">
                                <div>
                                    <div class="subtitle field-label">Номера телефонов, на которые будут приходить уведомления
                                        о плохих отзывах
                                    </div>
                                    <PhoneInputGroup
                                      inputType="phone"
                                      color="#525AD5"
                                      :can-delete-first-item="true"
                                      placeholder="+7 (987) 654-32-10"
                                      :value="get(settings, 'claim.claimPhone')"
                                      @input="updateSetting('claim.claimPhone', $event)"
                                      class="phone-group-input"
                                    />
                                </div>

                                <div v-if="get(settings, 'cancel')">
                                    <div class="subtitle field-label">Номера телефонов, на которые будут приходить уведомления
                                        об отменах
                                    </div>
                                    <PhoneInputGroup
                                      color="#525AD5"
                                      :can-delete-first-item="true"
                                      placeholder="+7 (987) 654-32-10"
                                      :value="get(settings, 'cancel.cancelPhone')"
                                      @input="updateSetting('cancel.cancelPhone', $event)"
                                      class="phone-group-input"
                                    />
                                </div>

                                <div v-if="botCrmConnector">
                                    <div class="d-flex align-center">
                                        <div class="subtitle">Группировать сообщения гостям по их визитам</div>
                                        <SmallHint class="ml-1" :text="visitGroupHintText" :size="16"></SmallHint>
                                    </div>
                                    <v-radio-group
                                      v-model="settings.groupByVisit"
                                      row
                                      class="mt-0"
                                    >
                                        <v-radio
                                          label="Да"
                                          :value="true"
                                          color="mainNew"
                                        ></v-radio>
                                        <v-radio
                                          label="Нет"
                                          :value="false"
                                          color="mainNew"
                                        ></v-radio>
                                    </v-radio-group>
                                </div>

                                <div v-if="yClientsConnector">
                                    <div class="d-flex align-center">
                                        <div class="subtitle">Отправлять только те уведомления YCLIENTS, которые
                                            начинаются с "#"
                                        </div>
                                    </div>
                                    <v-radio-group
                                      v-model="settings.smsBySpecialCharacter"
                                      row
                                      class="mt-0"
                                    >
                                        <v-radio
                                          label="Да"
                                          :value="true"
                                          color="mainNew"
                                        ></v-radio>
                                        <v-radio
                                          label="Нет"
                                          :value="false"
                                          color="mainNew"
                                        ></v-radio>
                                    </v-radio-group>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>
            <div v-if="!hideSaveButton" class="button-panel">
                <v-tooltip bottom max-width="300px" :disabled="isValid">
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" :style="{ display: 'inline-block' }">
                            <v-btn
                              elevation="0"
                              class="btn-save"
                              @click="save"
                              :loading="saveButtonLoading"
                              :disabled="!isValid"
                            >{{ saveButtonText }}</v-btn>
                        </span>
                    </template>
                    <span class="white--text">Заполните все обязательные поля</span>
                </v-tooltip>
            </div>
        </div>
        <SpinnerLoader v-if="loading"></SpinnerLoader>
    </div>
</template>

<script>
    import { defineComponent, ref, computed, onMounted, getCurrentInstance, watch } from 'vue'
    import get from 'lodash/get'
    import set from 'lodash/set'
    import isString from 'lodash/isString'
    import isPlainObject from 'lodash/isPlainObject'
    import isNil from 'lodash/isNil'
    import isUndefined from 'lodash/isUndefined'
    import omitBy from 'lodash/omitBy'
    import { v4 as uuidv4 } from 'uuid'
    import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
    import SpinnerLoader from '@/components/SpinnerLoader.vue'
    import TimeZonePicker from '@/components/ui/TimeZonePicker.vue'
    import SmallHint from '@/components/SmallHint.vue'
    import ValidationInput from '@/components/inputs/ValidationInput.vue'
    import phoneValidator, { isValidPhone } from '@/helpers/validators/phoneValidator'
    import { isValidTgGroupId } from '@/helpers/validators/tgGroupIdValidator'
    import { CONNECTOR_TYPES, PROVIDER_TYPES, CHANNEL_TYPES } from '@/vars/general'
    import PhoneInputGroup from '@/components/ui/PhoneInputGroup.vue'
    import CustomActivatorSelect from '@/components/inputs/CustomActivatorSelect.vue'
    import CommonSettingsBlock from '@/components/ClientNotifications/NotificationSettingsBlocks/CommonSettingsBlock.vue'
    import DeauthNotificationRecipientsBlock from '@/components/ClientNotifications/NotificationSettingsBlocks/DeauthNotificationRecipientsBlock.vue'
    import ServiceNotificationsBlock from '@/components/ClientNotifications/NotificationSettingsBlocks/ServiceNotificationsBlock.vue'
    import MarketingMailingsBlock from '@/components/ClientNotifications/NotificationSettingsBlocks/MarketingMailingsBlock.vue'
    import FieldStatusIcon from '@/components/ClientNotifications/NotificationSettingsBlocks/FieldStatusIcon.vue'
    import YclientsMessagesBlock from '@/components/ClientNotifications/NotificationSettingsBlocks/YclientsMessagesBlock.vue'
    import { useProject } from '@root/src/hooks/useProject'
    import api from '@/api/api'
    import cfg from '@root/config.js'
    import { mapWhatsappAppSettings, getDefaultWhatsappAppSettings } from '@/helpers/whatsapp'
    import { getCascadesByProject } from '@/api/networkFixer'
    import messages from '@dataheroes/messages'

    export default defineComponent({
        name: 'NotificationSettings',
        components: {
            BaseInputOutlined,
            SpinnerLoader,
            TimeZonePicker,
            SmallHint,
            ValidationInput,
            PhoneInputGroup,
            CustomActivatorSelect,
            CommonSettingsBlock,
            DeauthNotificationRecipientsBlock,
            ServiceNotificationsBlock,
            MarketingMailingsBlock,
            YclientsMessagesBlock,
            FieldStatusIcon,
        },
        emits: ['saved', 'save-state'],
        props: {
            connector: {
                type: Object,
                required: false,
                default: null,
            },
            displayMode: {
                type: String,
                default: 'default', // 'default' | 'notifications' | 'mailings' | 'sending-settings'
                validator: (value) => ['default', 'notifications', 'mailings', 'sending-settings'].includes(value),
            },
            onboardingMode: {
                type: Boolean,
                default: false,
            },
            /** При онбординге YC (канал сначала): id подключённого канала для defaultChannel/yclientsChannel */
            initialDefaultChannelId: {
                type: [String, Number],
                default: null,
            },
            hideSaveButton: {
                type: Boolean,
                default: false,
            },
        },
        setup(props, { emit }) {
            const root = getCurrentInstance().proxy
            const { project } = useProject()
            const loading = ref(true)
            const saveButtonLoading = ref(false)
            const settings = ref({})
            const deauthContacts = ref([])
            const initialDeauthContactIds = ref([])
            const initialDeauthContactsById = ref({})
            const initialDeauthContactsMetaById = ref({})
            const deauthAllGroups = ref([])
            const sendToReportRecipients = ref(false)
            const deauthAccessErrorMessage = ref('')
            /** Индексы строк deauth-контактов с активным запросом «Проверить». */
            const deauthVerifyingIndices = ref([])
            const sendingStepsIndexes = ref([0, 1, 2, 3]) // sending-settings: все блоки раскрыты, но их можно свернуть
            const stepsIndexesInit = ref([0, 1, 2])
            const wasTimezoneAdded = ref(false)
            const cascades = ref([]) // Каскады для текущего режима (notifications/mailings + custom для notifications)
            const activationSendChannelDraft = ref(null) // для маркетинговых рассылок (GCB2.settings.defaultActivationSendChannel)
            const defaultMessageVarsInit = ref({})
            const massenderLimitSettingsInit = ref(null)
            // Снимок настроек формы: после ensure whatsappBasis появляется connectorId → watch/fetchApp
            // подставляет дефолты и затирает ввод пользователя. Восстанавливаем снимок после fetchApp.
            const preservedSettingsOnConnectorAppear = ref(null)
            const suppressConnectorFetchApp = ref(false)

            const snapshotSettingsForConnectorAppear = () => {
                try {
                    preservedSettingsOnConnectorAppear.value = JSON.parse(
                        JSON.stringify(settings.value && typeof settings.value === 'object' ? settings.value : {}),
                    )
                } catch (e) {
                    preservedSettingsOnConnectorAppear.value = Object.assign({}, settings.value || {})
                }
            }

            const restorePreservedSettingsIfNeeded = () => {
                const preserved = preservedSettingsOnConnectorAppear.value
                if (!preserved || typeof preserved !== 'object') return
                settings.value = Object.assign({}, settings.value || {}, preserved)
                ensureRedirectDefaults()
            }

            const clearPreservedSettingsSnapshot = () => {
                preservedSettingsOnConnectorAppear.value = null
            }

            const sleepMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

            const withRetries = async (fn, { attempts = 3, delayMs = 1500, label = 'request' } = {}) => {
                let lastError = null
                for (let attempt = 1; attempt <= attempts; attempt++) {
                    try {
                        return await fn(attempt)
                    } catch (e) {
                        lastError = e
                        console.warn(`[NotificationSettingsBase] ${label}: attempt ${attempt}/${attempts} failed`, e)
                        if (attempt < attempts) {
                            await sleepMs(delayMs)
                        }
                    }
                }
                throw lastError
            }

            const ensureRedirectDefaults = () => {
                if (!settings.value || typeof settings.value !== 'object') {
                    settings.value = {}
                }
                if (!settings.value.redirect || typeof settings.value.redirect !== 'object') {
                    settings.value.redirect = {}
                }
                if (typeof settings.value.redirect.needToRedirect !== 'boolean') {
                    settings.value.redirect.needToRedirect = true
                }
                if (typeof settings.value.redirect.redirectPhone !== 'string') {
                    settings.value.redirect.redirectPhone = ''
                }
            }

            // Инициализируем redirect сразу, чтобы шаблон с v-model не падал до загрузки настроек
            ensureRedirectDefaults()

            const redirectMode = computed({
                get: () => {
                    try {
                        return get(settings.value, 'redirect.needToRedirect', true)
                    } catch (e) {
                        return true
                    }
                },
                set: (val) => {
                    updateSetting('redirect.needToRedirect', Boolean(val))
                },
            })

            const DEFAULT_MESSAGE_VAR_ID_BY_KEY = {
                salonName: '{название-салона}',
                salonAddress: '{адрес-салона}',
                salonPhone: '{контактный-телефон}',
                eventDate: '{дата-мероприятия}',
                reviewLink: '{ссылка-на-отзывы}',
                giftName: '{название-подарка}',
                bookingLink: '{ссылка-на-онлайн-запись}',
                headName: '{имя-руководителя}',
                adminName: '{имя-администратора}',
                masterName: '{наименование-специалиста}',
                ...Object.fromEntries(Array.from({ length: 3 }, (_, i) => [`masterName${i + 2}`, `{наименование-специалиста-${i + 2}}`])),
                ...Object.fromEntries(Array.from({ length: 49 }, (_, i) => [`giftName${i + 2}`, `{название-подарка-${i + 2}}`])),
                ...Object.fromEntries(Array.from({ length: 2 }, (_, i) => [`eventDate${i + 2}`, `{дата-мероприятия-${i + 2}}`])),
                serviceName: '{название-услуги}',
                ...Object.fromEntries(Array.from({ length: 4 }, (_, i) => [`serviceName${i + 2}`, `{название-услуги-${i + 2}}`])),
            }

            // Ключи новых переменных для отображения через v-for (не касается старых полей)
            const EXTRA_VAR_KEYS = [
                'headName', 'adminName',
                'masterName', ...Array.from({ length: 3 }, (_, i) => `masterName${i + 2}`),
                ...Array.from({ length: 49 }, (_, i) => `giftName${i + 2}`),
                ...Array.from({ length: 2 }, (_, i) => `eventDate${i + 2}`),
                'serviceName', ...Array.from({ length: 4 }, (_, i) => `serviceName${i + 2}`),
            ]

            const extraVarFields = computed(() =>
                EXTRA_VAR_KEYS
                    .filter(key => {
                        const initVal = defaultMessageVarsInit.value[key]
                        const currentVal = settings.value[key]
                        return (initVal !== undefined && initVal !== '') || (currentVal !== undefined && currentVal !== '')
                    })
                    .map(key => ({
                        key,
                        label: DEFAULT_MESSAGE_VAR_ID_BY_KEY[key].replace(/[{}]/g, '').replace(/^./, c => c.toUpperCase()),
                    }))
            )

            const hasBotConnector = computed(() => Boolean(props.connector?.connectorId))
            const messagingConnector = computed(() => {
                try {
                    return messages.getMessagingConnector(project.value?.connectors || [])
                } catch (e) {
                    return null
                }
            })
            const hasMessagingConnector = computed(() => Boolean(messagingConnector.value?.connectorType))
            const isSuperUser = computed(() => cfg.adminsWhitelist.includes(root?.$auth?.user?.sub))
            const canManageDeauthRecipients = computed(() => {
                const role = String(project.value?.myRole || '').toLowerCase()
                return role === 'owner' || role === 'admin'
            })

            // Если каскады/новая логика каналов выключена, не показываем и не валидируем
            // "Канал отправки маркетинговых рассылок (по умолчанию)"
            const isMailingsCascadeEnabled = computed(() => Boolean(project.value?.isChannelsRework))

            const stepsIndexes = computed({
                get: () => stepsIndexesInit.value,
                set: (value) => {
                    stepsIndexesInit.value = value
                },
            })

            const yClientsConnector = computed(() =>
                root.$store.getters
                    .projectById(root.$router.currentRoute.params.id)
                    .connectors.find((el) => el.connectorType === CONNECTOR_TYPES.yClients),
            )
            const altegioConnector = computed(() =>
                root.$store.getters
                    .projectById(root.$router.currentRoute.params.id)
                    .connectors.find((el) => el.connectorType === CONNECTOR_TYPES.altegio),
            )
            const botCrmConnector = computed(() => altegioConnector.value || yClientsConnector.value)

            const gcb2Connector = computed(() =>
                root.$store.getters
                    .projectById(root.$router.currentRoute.params.id)
                    .connectors.find((el) => el.connectorType === CONNECTOR_TYPES.GCB2),
            )

            const gcb2DefaultActivationSendChannel = computed(() => {
                // В разных местах/версиях бэка поле может лежать либо в settings, либо на верхнем уровне коннектора
                return (
                    get(gcb2Connector.value, 'settings.defaultActivationSendChannel') ??
                    get(gcb2Connector.value, 'defaultActivationSendChannel') ??
                    null
                )
            })

            const normalizeActivationSendChannel = (raw) => {
                if (!raw) return null

                let val = raw
                if (isString(val)) {
                    try {
                        val = JSON.parse(val)
                    } catch (e) {
                        // ignore
                    }
                }

                if (!isPlainObject(val)) return null

                const id =
                    !isNil(val.id)
                        ? val.id
                        : !isNil(val.channelId)
                            ? val.channelId
                            : !isNil(val.connectorId)
                                ? val.connectorId
                                : null

                if (isNil(id)) return null

                let type = val.type

                // Если type не указан (частый кейс в project settings), пытаемся вывести его автоматически
                if (!type) {
                    const isCascade = cascades.value?.some(c => c && String(c.id) === String(id))
                    if (isCascade) {
                        type = 'cascade'
                    } else {
                        type = 'channel'
                    }
                }

                return { type, id }
            }

            const effectiveActivationSendChannel = computed(() =>
                activationSendChannelDraft.value || normalizeActivationSendChannel(gcb2DefaultActivationSendChannel.value),
            )

            // Получаем список каналов отправки из проекта
            // Только CHANNEL_TYPES (WhatsApp рассылки автоматически исключается, так как имеет CONNECTOR_TYPES.whatsapp)
            const channels = computed(() => {
                if (!project.value?.isChannelsRework) return []

                const channelTypes = Object.values(CHANNEL_TYPES)
                return project.value.connectors.filter(c =>
                    !c.isChisai &&
                    c.connectorType !== CONNECTOR_TYPES.whatsappBasis && // Исключаем whatsappBasis
                    channelTypes.includes(c.connectorType),
                )
            })

            // Группы каналов для нумерации (только для CHANNEL_TYPES)
            const channelGroups = [
                {
                    name: 'whatsapp',
                    channelTypes: [CHANNEL_TYPES.whatsappChannel],
                },
                {
                    name: 'telegram',
                    channelTypes: [CHANNEL_TYPES.telegramChannel],
                },
                {
                    name: 'max',
                    channelTypes: [CHANNEL_TYPES.maxChannel],
                },
            ]

            // Получаем индекс канала в группе (как в таблице каналов ConnectorsListView)
            // ВАЖНО: индексируем только по connectorType из CHANNEL_TYPES и учитываем все каналы группы (включая DH),
            // чтобы имена "WhatsApp N" совпадали с таблицей каналов.
            const getChannelIndex = (connectorType, connectorId) => {
                if (!project.value || !project.value.connectors) return 1

                const group = channelGroups.find(g => g.channelTypes.includes(connectorType))
                if (!group) return 1

                const sortedChannels = project.value.connectors
                    .filter(c => !c.isChisai && c.connectorType === connectorType)
                    .sort((a, b) => {
                        const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                        const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                        if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) {
                            return aTime - bTime
                        }
                        if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
                        if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
                        return a.connectorId.localeCompare(b.connectorId)
                    })

                const currentPosition = sortedChannels.findIndex(c => c.connectorId === connectorId)
                return currentPosition >= 0 ? currentPosition + 1 : 1
            }

            // Получаем отображаемое имя канала
            const getChannelDisplayName = (connectorType, index) => {
                const baseNames = {
                    [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
                    [CHANNEL_TYPES.telegramChannel]: 'Telegram',
                    [CHANNEL_TYPES.maxChannel]: 'MAX',
                }
                const baseName = baseNames[connectorType] || connectorType

                if (index === 1) {
                    return baseName
                }
                return `${baseName} ${index}`
            }

            // Опции для выбора канала
            const channelOptions = computed(() => {
                const options = []

                // Убеждаемся, что project.value доступен
                if (!project.value || !project.value.connectors) {
                    return options
                }

                const dhWhatsappIds = new Set(
                    (project.value.connectors || [])
                        .filter(
                            (c) =>
                                c &&
                                c.connectorType === CHANNEL_TYPES.whatsappChannel &&
                                (c.providerType === PROVIDER_TYPES.whatsapp),
                        )
                        .map((c) => String(c.channelId || c.connectorId)),
                )

                channels.value.forEach(connector => {
                    // Не показываем WhatsApp-каналы с провайдером DH в сервисных уведомлениях
                    if (connector.connectorType === CHANNEL_TYPES.whatsappChannel) {
                        const key = String(connector.channelId || connector.connectorId)
                        if (dhWhatsappIds.has(key)) {
                            return
                        }
                        const directProvider = connector.providerType
                        let fullProvider = null
                        try {
                            const getter = root?.$store?.getters?.connectorById
                            const full = typeof getter === 'function' ? getter(connector.connectorId) : null
                            fullProvider = full?.providerType ?? null
                        } catch (e) {
                            // ignore
                        }
                        if (directProvider === PROVIDER_TYPES.whatsapp || fullProvider === PROVIDER_TYPES.whatsapp) {
                            return
                        }
                    }
                    const connectorType = connector.connectorType
                    const index = getChannelIndex(connectorType, connector.connectorId)
                    const displayName = getChannelDisplayName(connectorType, index)
                    const channelId = connector.channelId || connector.connectorId
                    const phone = connector.phone
                    const label = phone ? `${displayName} (${phone})` : displayName

                    options.push({
                        label,
                        value: JSON.stringify({ type: 'channel', id: channelId }),
                    })
                })

                // Для сервисных уведомлений доступны только notifications-каскады
                cascades.value
                    .filter(c => c && c.cascadeType === 'notifications')
                    .forEach(c => {
                        options.push({
                        label: 'Каскад',
                        value: JSON.stringify({ type: 'cascade', id: c.id }),
                    })
                    })

                return options
            })

            // Опции для маркетинговых рассылок: каналы + каскады mailings
            const mailingsChannelOptions = computed(() => {
                const options = []

                if (!project.value || !project.value.connectors) {
                    return options
                }

                // Каналы
                channels.value.forEach(connector => {
                    const connectorType = connector.connectorType
                    const index = getChannelIndex(connectorType, connector.connectorId)
                    const displayName = getChannelDisplayName(connectorType, index)
                    const channelId = connector.channelId || connector.connectorId
                    const phone = connector.phone
                    const label = phone ? `${displayName} (${phone})` : displayName

                    options.push({
                        label,
                        value: JSON.stringify({ type: 'channel', id: channelId }),
                    })
                })

                // Каскады mailings
                cascades.value
                    .filter(c => c && c.cascadeType === 'mailings')
                    .forEach(c => {
                        options.push({
                            label: 'Каскад',
                            value: JSON.stringify({ type: 'cascade', id: c.id }),
                        })
                    })

                return options
            })

            const selectedActivationSendChannel = computed(() => {
                const current = effectiveActivationSendChannel.value
                if (!current) return null

                const options = mailingsChannelOptions.value
                const matchingOption = options.find(option => {
                    if (!option.value) return false
                    try {
                        const optionData = JSON.parse(option.value)
                        // current.type может отсутствовать в данных из бэка — нормализуем выше, но на всякий случай
                        const currentType = current.type || optionData.type
                        return String(optionData.id) === String(current.id) && optionData.type === currentType
                    } catch (e) {
                        return false
                    }
                })

                if (matchingOption) {
                    return matchingOption.value
                }

                return JSON.stringify(current)
            })

            const updateActivationSendChannel = (value) => {
                if (value === null) {
                    activationSendChannelDraft.value = null
                    return
                }

                // Иногда селекты могут эмитить уже объект, а не строку
                if (isPlainObject(value) && value.type && !isNil(value.id)) {
                    activationSendChannelDraft.value = normalizeActivationSendChannel(value)
                    return
                }

                try {
                    const channelData = JSON.parse(value)
                    activationSendChannelDraft.value = normalizeActivationSendChannel(channelData)
                } catch (e) {
                    console.error('Ошибка при парсинге канала маркетинговых рассылок:', String(e))
                    activationSendChannelDraft.value = null
                }
            }

            // Выбранный канал
            const selectedChannel = computed(() => {
                if (!settings.value.defaultChannel) {
                    return null
                }

                const defaultChannel = settings.value.defaultChannel
                const channelId = defaultChannel.id
                const options = channelOptions.value

                // Ищем соответствующее значение в channelOptions по id и типу
                const matchingOption = options.find(option => {
                    if (!option.value) return false
                    try {
                        const optionData = JSON.parse(option.value)
                        // Сравниваем id (может быть строкой или числом)
                        const idMatch = String(optionData.id) === String(channelId)
                        // Для каскадов обязательно проверяем тип
                        if (defaultChannel.type === 'cascade' || optionData.type === 'cascade') {
                            return idMatch && optionData.type === defaultChannel.type
                        }
                        // Для каналов проверяем тип, если он указан
                        const typeMatch = !optionData.type || !defaultChannel.type || optionData.type === defaultChannel.type
                        return idMatch && typeMatch
                    } catch (e) {
                        return false
                    }
                })

                // Если нашли совпадение, возвращаем его value
                if (matchingOption) {
                    return matchingOption.value
                }

                // Если не нашли, но defaultChannel существует, пытаемся найти канал напрямую
                // Это может быть полезно, если канал был удален, но значение сохранено
                // Или если данные еще не загружены
                const directChannel = channels.value.find(c => {
                    const cChannelId = c.channelId
                    const cConnectorId = c.connectorId
                    // Сравниваем и с channelId, и с connectorId
                    return (cChannelId && String(cChannelId) === String(channelId)) ||
                        (cConnectorId && String(cConnectorId) === String(channelId))
                })

                if (directChannel) {
                    const connectorType = directChannel.connectorType
                    const index = getChannelIndex(connectorType, directChannel.connectorId)
                    const displayName = getChannelDisplayName(connectorType, index)
                    const channelIdValue = directChannel.channelId || directChannel.connectorId
                    // Формируем значение в том же формате, что и в channelOptions
                    return JSON.stringify({ type: 'channel', id: channelIdValue })
                }

                // В последнюю очередь возвращаем сохраненное значение
                // Это может быть полезно, если канал был удален, но значение сохранено
                return JSON.stringify(defaultChannel)
            })

            // Обновление канала
            const updateChannel = (value) => {
                if (value === null) {
                    updateSetting('defaultChannel', null)
                } else {
                    try {
                        const channelData = JSON.parse(value)
                        updateSetting('defaultChannel', channelData)
                    } catch (e) {
                        console.error('Ошибка при парсинге канала:', e)
                        updateSetting('defaultChannel', null)
                    }
                }
            }

            // Опции для выбора канала отправки сообщений из YCLIENTS (коды подтверждения)
            const yclientsChannelOptions = computed(() => {
                const options = []

                // Каналы из проекта
                if (project.value?.connectors) {
                    channels.value.forEach(connector => {
                        const connectorType = connector.connectorType
                        const index = getChannelIndex(connectorType, connector.connectorId)
                        const displayName = getChannelDisplayName(connectorType, index)
                        const channelId = connector.channelId || connector.connectorId
                        const phone = connector.phone
                        const label = phone ? `${displayName} (${phone})` : displayName

                        options.push({
                            label,
                            value: JSON.stringify({ type: 'channel', id: channelId }),
                        })
                    })
                }

                // Первым пунктом добавляем каскад для YCLIENTS (crmMessages), если есть
                const crmCascade = cascades.value.find(c => c && c.cascadeType === 'crmMessages')
                if (crmCascade) {
                    options.unshift({
                        label: 'Каскад',
                        value: JSON.stringify({ type: 'cascade', id: crmCascade.id }),
                    })
                }

                return options
            })

            // Для Yclients канала
            const selectedYclientsChannel = computed(() => {
                if (!settings.value.yclientsChannel) {
                    return null
                }
                const yclientsChannel = settings.value.yclientsChannel
                const channelId = yclientsChannel.id

                const options = yclientsChannelOptions.value
                const matchingOption = options.find(option => {
                    if (!option.value) return false
                    try {
                        const optionData = JSON.parse(option.value)
                        return String(optionData.id) === String(channelId)
                    } catch (e) {
                        return false
                    }
                })

                if (matchingOption) {
                    return matchingOption.value
                }

                return JSON.stringify(yclientsChannel)
            })

            const updateYclientsChannel = (value) => {
                if (value === null) {
                    updateSetting('yclientsChannel', null)
                } else {
                    try {
                        const channelData = JSON.parse(value)
                        updateSetting('yclientsChannel', channelData)
                    } catch (e) {
                        console.error('Ошибка при парсинге Yclients канала:', e)
                        updateSetting('yclientsChannel', null)
                    }
                }
            }

            const requiredFields = computed(() => {
                // Режим mailings: UI не показывает сервисные поля, поэтому список обязательных полей должен быть отдельный
                if (props.displayMode === 'mailings') {
                    const fields = [
                        { name: 'salonName', isPhone: false },
                        { name: 'salonAddress', isPhone: false },
                        { name: 'salonPhone', isPhone: true },
                    ]
                    if (isMailingsCascadeEnabled.value) {
                        fields.push({ name: 'defaultActivationSendChannel', isExternal: true })
                    }
                    // if (hasBotConnector.value) {
                    //     fields.push({ name: 'phoneForNotification', isPhone: true })
                    // }
                    return fields
                }
                // Когда бот недоступен, на вкладке "Настройки отправки" сохраняем только дефолтные переменные (через GCB2)
                if (props.displayMode === 'sending-settings' && !hasBotConnector.value) {
                    return [
                        { name: 'salonName', isPhone: false },
                        { name: 'salonAddress', isPhone: false },
                        { name: 'salonPhone', isPhone: true },
                    ]
                }
                // Когда бот недоступен, в режиме mailings доступны переменные + канал рассылок (GCB2)
                if (props.displayMode === 'mailings' && !hasBotConnector.value) {
                    return [
                        { name: 'salonName', isPhone: false },
                        { name: 'salonAddress', isPhone: false },
                        { name: 'salonPhone', isPhone: true },
                        ...(isMailingsCascadeEnabled.value ? [{ name: 'defaultActivationSendChannel', isExternal: true }] : []),
                    ]
                }
                const fields = [
                    { name: 'redirect.needToRedirect', isPhone: false },
                    { name: 'salonName', isPhone: false },
                    { name: 'salonAddress', isPhone: false },
                    { name: 'salonPhone', isPhone: true },
                    // { name: 'phoneForNotification', isPhone: true },
                    { name: 'timezone', isPhone: false },
                    { name: 'defaultChannel', isPhone: false },
                ]
                if (get(settings.value, 'redirect.needToRedirect')) {
                    fields.push({ name: 'redirect.redirectPhone', isPhone: true })
                }
                // Для маркетинговых рассылок defaultChannel берется из GCB2 коннектора
                if (props.displayMode === 'mailings') {
                    const idx = fields.findIndex(f => f.name === 'defaultChannel')
                    if (idx !== -1) fields.splice(idx, 1)
                    if (isMailingsCascadeEnabled.value) {
                        fields.push({ name: 'defaultActivationSendChannel', isExternal: true })
                    }
                }
                return fields
            })

            const optionalPhoneFields = computed(() => {
                if (!settings.value.phone) return []
                const fields = ['claim.claimPhone']
                if (get(settings.value, 'cancel')) {
                    fields.push('cancel.cancelPhone')
                }
                return fields
            })

            const getRequiredFieldValue = (field) => {
                if (field.isExternal && field.name === 'defaultActivationSendChannel') {
                    return effectiveActivationSendChannel.value
                }
                return get(settings.value, field.name)
            }

            const requiredFieldIsValid = (field) => {
                const value = getRequiredFieldValue(field)
                if (field.isExternal && field.name === 'defaultActivationSendChannel') {
                    return Boolean(value)
                }
                if (field.isPhone) {
                    if (!field.isPhoneArray) {
                        return isValidPhone(value)
                    } else {
                        const phoneArr = value
                        return phoneArr && phoneArr.every(el => isValidPhone(el))
                    }
                } else {
                    return !isNil(value) && value !== ''
                }
            }

            const optionalPhoneFieldIsValid = (fieldName) => {
                const phoneArr = get(settings.value, fieldName)
                if (isNil(phoneArr)) return true
                if (!Array.isArray(phoneArr)) return false
                return phoneArr
                    .filter(phone => String(phone ?? '').trim() !== '')
                    .every(phone => isValidPhone(phone))
            }

            const isValid = computed(() =>
                requiredFields.value.every(requiredFieldIsValid) &&
                optionalPhoneFields.value.every(optionalPhoneFieldIsValid),
            )

            const mainSettingsValid = computed(() => {
                if (props.displayMode === 'sending-settings' && !hasBotConnector.value) {
                    return true
                }
                const exp1 = Boolean(settings.value['timezone'])
                const exp2 = get(settings.value, 'redirect.needToRedirect') ? isValidPhone(get(settings.value, 'redirect.redirectPhone')) : true
                return exp1 && exp2
            })

            const informationSettingsValid = computed(() => {
                const exp1 = Boolean(get(settings.value, 'salonName')) && get(settings.value, 'salonName').trim().length > 0
                const exp2 = Boolean(get(settings.value, 'salonAddress')) && get(settings.value, 'salonAddress').trim().length > 0
                const exp3 = Boolean(get(settings.value, 'salonPhone')) && isValidPhone(get(settings.value, 'salonPhone'))
                if (props.displayMode === 'sending-settings' && !hasBotConnector.value) {
                    return exp1 && exp2 && exp3
                }
                if (props.displayMode === 'mailings' && !hasBotConnector.value) {
                    return isMailingsCascadeEnabled.value
                        ? (exp1 && exp2 && exp3 && Boolean(effectiveActivationSendChannel.value))
                        : (exp1 && exp2 && exp3)
                }
                // const exp4 = Boolean(settings.value['phoneForNotification']) && isValidPhone(settings.value['phoneForNotification'])
                const exp5 = props.displayMode === 'mailings'
                    ? (isMailingsCascadeEnabled.value ? Boolean(effectiveActivationSendChannel.value) : true)
                    : Boolean(settings.value['defaultChannel'])
                // return exp1 && exp2 && exp3 && exp4 && exp5
                return exp1 && exp2 && exp3 && exp5
            })

            const saveButtonText = computed(() => 'Сохранить')
            watch(
                [isValid, saveButtonLoading],
                () => {
                    emit('save-state', {
                        canSave: Boolean(isValid.value),
                        saveLoading: Boolean(saveButtonLoading.value),
                    })
                },
                { immediate: true },
            )
            const hasPendingDeauthChanges = computed(() => {
                const normalize = (v) => String(v ?? '').trim()
                const contacts = deauthContacts.value || []
                return contacts.some((contact) => {
                    const id = String(contact?.id ?? '').trim()
                    const currentValue = normalize(contact?.contactValue)
                    if (!id) return currentValue.length > 0
                    const initialValue = normalize(initialDeauthContactsById.value?.[id])
                    return currentValue !== initialValue
                })
            })

            const modeHintText = computed(() => {
                if (settings.value.redirect?.needToRedirect) {
                    return 'В тестовом режиме все сервисные сообщения (например, сообщения о создании, подтверждении, изменении записи) будут отправляться на номер, который необходимо указать в поле ниже'
                } else {
                    return 'В боевом режиме все сервисные сообщения (например, сообщения о создании, подтверждении, изменении записи) будут отправляться вашим гостям. Убедитесь, что вы точно готовы его включить'
                }
            })
            const redirectPhoneHintText = 'Номер телефона, на который будут отправляться сервисные сообщения (например, сообщения о создании, подтверждении, изменении записии) в тестовом режиме'
            const visitGroupHintText = `Если гость записан на несколько процедур в один день и между процедурами разница по времени не более 30 минут, то данная функция позволяет сгруппировать сервисные сообщения (например, сообщения о создании, подтверждении, изменении записии) по его визитам в одно сообщение`
            const informationSettingsHintText = `Обычно информация в этих поля подтягивается из CRM-системы. Проверьте, что все подтянулось корректно и при необходимости скорректируйте или заполните поля`

            const updateSetting = (key, value) => {
                const tempObj = Object.assign({}, settings.value)
                set(tempObj, key, value)
                settings.value = Object.assign({}, settings.value ?? {}, tempObj)
            }

            const setTimezone = (evt) => {
                wasTimezoneAdded.value = true
                updateSetting('timezone', evt)
                // При выборе часового пояса сразу сохраняем таймзону проекта (Firebase)
                const projectId = String(
                    project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id || '',
                )
                if (projectId && evt) {
                    ;(async () => {
                        try {
                            await api.networkFixer.updateProjectTimezone({ projectId, timezone: String(evt) })
                        } catch (e) {
                            // Не спамим ошибками в онбординге
                            if (!props.onboardingMode) {
                                root.$store.dispatch('callNotify', 'Ошибка при сохранении часового пояса')
                            }
                        }
                    })()
                }
            }

            const save = async () => {
                const logPrefix = '[NotificationSettingsBase.save]'
                saveButtonLoading.value = true
                try {
                    const projectId = String(
                        project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id || '',
                    )
                    const connectorId = props.connector?.connectorId || null
                    const hasApp = Boolean(props.connector?.appCodeImmutable)
                    const yClientsConnectorId = yClientsConnector.value?.connectorId || null
                    const crmConnector = botCrmConnector.value
                    const crmConnectorId = crmConnector?.connectorId || null

                    // Для маркетинговых рассылок сохраняем канал через /projects/updateConnectorSettings (GCB2 коннектор)
                    if ((props.displayMode === 'mailings' || props.displayMode === 'sending-settings') && isMailingsCascadeEnabled.value) {
                        // В sending-settings не шлём null, чтобы не затирать значение, если пользователь его не менял
                        const shouldSendActivationChannel =
                            props.displayMode === 'mailings'
                                ? true
                                : activationSendChannelDraft.value !== null

                        if (shouldSendActivationChannel) {
                            const channelToSave = effectiveActivationSendChannel.value
                            if (gcb2Connector.value?.connectorId && channelToSave) {
                                await root.$store.dispatch('project/updateConnectorSettings', {
                                    connectorId: gcb2Connector.value.connectorId,
                                    settings: {
                                        defaultActivationSendChannel: channelToSave,
                                    },
                                })
                                activationSendChannelDraft.value = null
                            } else if (props.displayMode === 'mailings') {
                                root.$store.dispatch(
                                    'callNotify',
                                    'Не найден коннектор GCB2 или не выбран канал для сохранения настроек рассылок',
                                )
                            }
                        }
                    }

                    // Сохраняем дефолтные переменные через GCB2 (и когда бот есть, и когда бота нет)
                    if (props.displayMode === 'sending-settings' || props.displayMode === 'mailings') {
                        const updates = []
                        Object.keys(DEFAULT_MESSAGE_VAR_ID_BY_KEY).forEach((key) => {
                            const varId = DEFAULT_MESSAGE_VAR_ID_BY_KEY[key]
                            const nextValue = String(get(settings.value, key) ?? '')
                            const prevValue = String(get(defaultMessageVarsInit.value, key) ?? '')
                            if (nextValue !== prevValue) {
                                updates.push({
                                    id: '_DEFAULT_MESSAGE_VAR',
                                    _id: uuidv4(),
                                    value: nextValue,
                                    varId,
                                    isDefault: true,
                                })
                            }
                        })

                        if (updates.length > 0 && projectId) {
                            const { error } = await api.gcb2.updateDefaultMessageVarsValues({
                                projectId,
                                messageVars: updates,
                            })
                            if (error) {
                                root.$store.dispatch('callNotify', 'Ошибка при сохранении переменных по умолчанию')
                            } else {
                                // фиксируем сохраненные значения как "исходные"
                                const nextInit = Object.assign({}, defaultMessageVarsInit.value)
                                Object.keys(DEFAULT_MESSAGE_VAR_ID_BY_KEY).forEach((key) => {
                                    nextInit[key] = String(get(settings.value, key) ?? '')
                                })
                                defaultMessageVarsInit.value = nextInit
                            }
                        }

                        // Лимит пакетной отправки сохраняем через massender/limits/settings (как BatchMode)
                        try {
                            if (hasMessagingConnector.value && projectId) {
                                const nextLimit = String(get(settings.value, 'dailyLimit') ?? '')
                                const prevLimit = String(
                                    get(massenderLimitSettingsInit.value, 'default_daily_limit_of_messages_items') ?? '',
                                )
                                if (nextLimit !== prevLimit) {
                                    const base = massenderLimitSettingsInit.value || {
                                        timezone_offset: '0',
                                        default_daily_limit_of_messages_items: prevLimit,
                                    }
                                    const settingsToSave = Object.assign({}, base, {
                                        default_daily_limit_of_messages_items: nextLimit,
                                    })
                                    const { error } = await api.massender.saveLimitSettings({
                                        projectId,
                                        connectorType: messagingConnector.value.connectorType,
                                        settings: settingsToSave,
                                    })
                                    if (error) {
                                        root.$store.dispatch(
                                            'callNotify',
                                            'Ошибка при сохранении лимита пакетной отправки',
                                        )
                                    } else {
                                        massenderLimitSettingsInit.value = Object.assign({}, settingsToSave)
                                    }
                                }
                            }
                        } catch (e) {
                        }
                    }

                    // Если бот доступен — создаём приложение (createApp) или обновляем (updateApp).
                    // appCodeImmutable появляется только после createApp; без него updateApp даёт 500.
                    // emit('saved') в онбординге вызывается ТОЛЬКО при успехе createApp+updateApp / updateApp.
                    if (connectorId) {
                        if (!hasApp && crmConnectorId) {
                            // Коннектор создан (в т.ч. при онбординге), но приложение в networkFixer ещё нет — создаём, как в whatsappBasis/Settings.vue
                            const salonId = Number(crmConnector?.companyIds?.[0] ?? 0)
                            const defaultSettings = await getDefaultWhatsappAppSettings({
                                connectorId,
                                yClientsConnectorId: altegioConnector.value
                                    ? undefined
                                    : yClientsConnectorId,
                                altegioConnectorId: altegioConnector.value?.connectorId,
                                salonId: salonId || 0,
                                channel: {},
                            })
                            const merged = Object.assign({}, defaultSettings, settings.value)
                            let createData = mapWhatsappAppSettings(merged, 'create')
                            if (merged.onlyForMailing) {
                                createData.salonId = 0
                            } else if (createData.salonId === 0) {
                                createData.salonId = -1
                            }
                            if (project.value?.isChannelsRework) {
                                createData.isUseChannels = true
                            }
                            const { error } = await api.networkFixer.createApp({
                                crmConnectorId,
                                connectorId,
                                data: createData,
                            })
                            if (error) {
                                console.error(logPrefix, 'createApp failed, emit(saved) skipped', error)
                                root.$store.dispatch('callNotify', 'Ошибка при сохранении настроек')
                            } else {
                                // После успешного создания приложения сразу обновляем его через app/update,
                                // чтобы применить актуальные настройки (включая выбранный канал).
                                const appData = omitBy(mapWhatsappAppSettings(settings.value, 'edit'), 'triggers')
                                if (settings.value.onlyForMailing) {
                                    appData.salonId = 0
                                } else if (appData.salonId === 0) {
                                    appData.salonId = -1
                                }
                                if (project.value?.isChannelsRework) {
                                    appData.isUseChannels = true
                                }
                                const { error: updateError } = await api.networkFixer.updateApp({
                                    connectorId,
                                    data: appData,
                                })
                                if (updateError) {
                                    console.error(logPrefix, 'updateApp after createApp failed, emit(saved) skipped', updateError)
                                    root.$store.dispatch('callNotify', 'Ошибка при сохранении настроек')
                                } else {
                                    root.$store.dispatch('callNotify', 'Настройки успешно сохранены')
                                    await root.$store.dispatch('project/fetchProject', project.value.id)
                                    if (props.onboardingMode) {
                                        emit('saved', settings.value)
                                    }
                                }
                            }
                        } else {
                            // Важно: сюда попадаем и когда hasApp=false, но нет yClientsConnector —
                            // тогда updateApp без createApp может дать 500, и emit(saved) не вызовется.
                            if (!hasApp && !crmConnectorId) {
                                console.warn(logPrefix, 'updateApp without app and without crm connector', {
                                    connectorId,
                                })
                            }
                            const appData = omitBy(mapWhatsappAppSettings(settings.value, 'edit'), 'triggers')
                            if (settings.value.onlyForMailing) {
                                appData.salonId = 0
                            } else if (appData.salonId === 0) {
                                appData.salonId = -1
                            }
                            const { error } = await api.networkFixer.updateApp({
                                connectorId,
                                data: appData,
                            })
                            if (error) {
                                console.error(logPrefix, 'updateApp failed, emit(saved) skipped', error)
                                root.$store.dispatch('callNotify', 'Ошибка при сохранении настроек')
                            } else {
                                root.$store.dispatch('callNotify', 'Настройки успешно сохранены')
                                await root.$store.dispatch('project/fetchProject', project.value.id)
                                if (props.onboardingMode) {
                                    emit('saved', settings.value)
                                }
                            }
                        }
                    } else if (props.onboardingMode && projectId) {
                        // Онбординг без whatsappBasis: один раз пробуем найти/создать коннектор.
                        // App и emit('saved') — только на следующем нажатии «Сохранить», когда connector уже в props.
                        console.warn(logPrefix, 'onboarding: no connectorId, ensuring whatsappBasis', { projectId })
                        // Сохраняем ввод пользователя: иначе появление connectorId → fetchApp затрёт форму.
                        // suppress оставляем true после успеха, чтобы watch не перезаписал settings дефолтами.
                        snapshotSettingsForConnectorAppear()
                        suppressConnectorFetchApp.value = true
                        try {
                            await root.$store.dispatch('project/fetchProject', project.value.id)
                            const currentProject =
                                root.$store.getters.projectById?.(project.value.id) || project.value
                            const existingWb = (currentProject?.connectors || []).find(
                                (c) => c && c.connectorType === CONNECTOR_TYPES.whatsappBasis && c.connectorId,
                            )
                            if (existingWb) {
                                restorePreservedSettingsIfNeeded()
                                clearPreservedSettingsSnapshot()
                                root.$store.dispatch(
                                    'callNotify',
                                    'Коннектор WhatsApp найден. Нажмите «Сохранить» ещё раз.',
                                )
                                return
                            }

                            await withRetries(
                                async (attempt) => {
                                    if (attempt > 1) {
                                        await root.$store.dispatch('project/fetchProject', project.value.id)
                                        const refreshed =
                                            root.$store.getters.projectById?.(project.value.id) || project.value
                                        const existing = (refreshed?.connectors || []).find(
                                            (c) =>
                                                c &&
                                                c.connectorType === CONNECTOR_TYPES.whatsappBasis &&
                                                c.connectorId,
                                        )
                                        if (existing) return existing
                                    }
                                    const createdConnector = await root.$store.dispatch('project/createConnector', {
                                        projectId,
                                        connectorType: CONNECTOR_TYPES.whatsappBasis,
                                    })
                                    const addResult = await root.$store.dispatch(
                                        'project/addConnector',
                                        createdConnector,
                                    )
                                    if (addResult?.error) {
                                        throw addResult.error instanceof Error
                                            ? addResult.error
                                            : new Error(
                                                  addResult.error?.message ||
                                                      'Не удалось создать коннектор WhatsApp',
                                              )
                                    }
                                    await root.$store.dispatch('project/setProjects')
                                    await root.$store.dispatch('project/fetchProject', project.value.id)
                                    return createdConnector
                                },
                                { attempts: 3, delayMs: 1500, label: 'create whatsappBasis' },
                            )
                            restorePreservedSettingsIfNeeded()
                            clearPreservedSettingsSnapshot()
                            root.$store.dispatch(
                                'callNotify',
                                'Коннектор WhatsApp создан. Нажмите «Сохранить» ещё раз.',
                            )
                            return
                        } catch (e) {
                            console.error(logPrefix, 'ensure whatsappBasis failed', e)
                            suppressConnectorFetchApp.value = false
                            clearPreservedSettingsSnapshot()
                            root.$store.dispatch(
                                'callNotify',
                                'Ошибка при создании коннектора WhatsApp. Нажмите «Сохранить» ещё раз.',
                            )
                            return
                        }
                    } else {
                        // Бота нет — обновим проект, чтобы актуализировать данные в store (GCB2/massender)
                        if (projectId) {
                            await root.$store.dispatch('project/fetchProject', project.value.id)
                        }
                    }

                    const normalizeDeauthValue = (v) => String(v ?? '').trim()
                    const allContacts = deauthContacts.value || []
                    const valueIndexesMap = new Map()
                    allContacts.forEach((c, index) => {
                        const val = normalizeDeauthValue(c?.contactValue)
                        if (!val) return
                        const indexes = valueIndexesMap.get(val) || []
                        indexes.push(index)
                        valueIndexesMap.set(val, indexes)
                    })
                    const duplicateValues = new Set(
                        Array.from(valueIndexesMap.entries())
                            .filter(([, indexes]) => Array.isArray(indexes) && indexes.length > 1)
                            .map(([value]) => value)
                    )
                    const unsavedDuplicateIndexes = new Set(
                        allContacts
                            .map((c, index) => ({ c, index }))
                            .filter(({ c }) => !String(c?.id ?? '').trim())
                            .filter(({ c }) => duplicateValues.has(normalizeDeauthValue(c?.contactValue)))
                            .map(({ index }) => index)
                    )

                    const modifiedSavedDeauthContacts = (deauthContacts.value || []).filter((c) => {
                        const id = String(c?.id ?? '').trim()
                        if (!id) return false
                        const currentValue = normalizeDeauthValue(c?.contactValue)
                        if (!currentValue) return false
                        const initialValue = normalizeDeauthValue(initialDeauthContactsById.value?.[id])
                        return currentValue !== initialValue
                    })
                    for (const contact of modifiedSavedDeauthContacts) {
                        const id = String(contact?.id ?? '').trim()
                        const contactValue = normalizeDeauthValue(contact?.contactValue)
                        if (!id || !contactValue) continue
                        await api.deauthNotification.updateContact({
                            projectId,
                            id,
                            contactValue,
                        })
                    }

                    // Сохраняем новые деавторизационные контакты (без id, непустые)
                    const unsavedDeauthContacts = (deauthContacts.value || []).filter(
                        (c, index) =>
                            !c?.id &&
                            String(c?.contactValue ?? '').trim().length > 0 &&
                            !unsavedDuplicateIndexes.has(index)
                    )
                    for (const contact of unsavedDeauthContacts) {
                        const normalizedValue = String(contact.contactValue ?? '').trim()
                        await api.deauthNotification.updateContacts({
                            projectId,
                            contactValue: normalizedValue,
                        })
                    }
                    // Удаляем пустые несохранённые поля из списка
                    deauthContacts.value = (deauthContacts.value || []).filter(
                        (c, index) =>
                            (c?.id || String(c?.contactValue ?? '').trim().length > 0) &&
                            !unsavedDuplicateIndexes.has(index)
                    )
                    if (
                        unsavedDeauthContacts.length > 0 ||
                        modifiedSavedDeauthContacts.length > 0 ||
                        unsavedDuplicateIndexes.size > 0
                    ) {
                        await fetchDeauthSettingsIfNeeded()
                    }
                } catch (error) {
                    console.error(logPrefix, 'save exception', error)
                    root.$store.dispatch('callNotify', 'Ошибка при сохранении настроек')
                } finally {
                    saveButtonLoading.value = false
                }
            }

            const deauthContactsValue = ref([])
            watch(
                deauthContacts,
                (contacts) => {
                    deauthContactsValue.value = (contacts || []).map((c) => String(c?.contactValue ?? ''))
                },
                { immediate: true, deep: true }
            )

            const normalizeDeauthContacts = (contacts) => {
                if (!Array.isArray(contacts) || contacts.length === 0) return []
                return contacts
                    .filter((item) => item && String(item?.contactValue ?? '').trim().length > 0)
                    .map((item) => ({
                        id: item.id ?? null,
                        contactType: item.contactType ?? null,
                        contactValue: String(item.contactValue ?? '').trim(),
                        displayName: item.displayName ?? null,
                        isVerified: typeof item?.isVerified === 'boolean'
                            ? item.isVerified
                            : (typeof item?.verified === 'boolean' ? item.verified : null),
                        // Флаг отображения "Отправка недоступна":
                        // на первичной загрузке не показываем ошибку, только после нажатия "Проверить..."
                        isVerificationChecked: false,
                        // Если существующий контакт был изменен в UI, здесь храним старый id,
                        // который нужно удалить на бэке перед созданием нового контакта.
                        pendingRemovalId: null,
                    }))
            }

            const getDeauthProjectId = () =>
                String(project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id || '')

            /** Сохраняет порядок строк UI при подтягивании ответа сервера (иначе свежесохранённый контакт «прыгает» вверх). */
            const mergeDeauthContactsPreservingOrder = (previousContacts, serverContacts, mergedUnsavedContacts) => {
                const serverPool = [...serverContacts]
                const unsavedPool = [...mergedUnsavedContacts]
                const result = []

                const takeFromServerById = (id) => {
                    const sid = String(id ?? '')
                    if (!sid) return null
                    const idx = serverPool.findIndex((c) => String(c?.id ?? '') === sid)
                    if (idx === -1) return null
                    return serverPool.splice(idx, 1)[0]
                }
                const takeFromServerByValue = (value) => {
                    const v = String(value ?? '').trim()
                    if (!v) return null
                    const idx = serverPool.findIndex((c) => String(c?.contactValue ?? '').trim() === v)
                    if (idx === -1) return null
                    return serverPool.splice(idx, 1)[0]
                }
                const takeFromUnsavedByValue = (value) => {
                    const v = String(value ?? '').trim()
                    if (!v) return null
                    const idx = unsavedPool.findIndex((c) => String(c?.contactValue ?? '').trim() === v)
                    if (idx === -1) return null
                    return unsavedPool.splice(idx, 1)[0]
                }

                for (const prev of previousContacts || []) {
                    const prevId = String(prev?.id ?? '')
                    const prevValue = String(prev?.contactValue ?? '').trim()

                    if (!prevId && !prevValue) {
                        result.push({
                            id: null,
                            contactType: null,
                            contactValue: '',
                            displayName: null,
                            isVerified: null,
                            isVerificationChecked: false,
                            pendingRemovalId: prev?.pendingRemovalId ?? null,
                        })
                        continue
                    }

                    let next = null
                    if (prevId) {
                        next = takeFromServerById(prevId)
                    }
                    if (!next && prevValue) {
                        next = takeFromUnsavedByValue(prevValue) || takeFromServerByValue(prevValue)
                    }
                    if (next) {
                        result.push(next)
                    }
                }

                result.push(...serverPool, ...unsavedPool)
                return result
            }

            const fetchDeauthSettingsIfNeeded = async () => {
                if (!(props.displayMode === 'sending-settings' || props.displayMode === 'mailings' || props.displayMode === 'notifications' || props.displayMode === 'default')) return

                const projectId = String(
                    project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id || '',
                )
                if (!projectId) return

                try {
                    const previousContacts = deauthContacts.value || []
                    const previousById = new Map(
                        previousContacts
                            .filter((item) => item && item.id)
                            .map((item) => [String(item.id), item])
                    )
                    const previousByValue = new Map(
                        previousContacts
                            .filter((item) => item && String(item.contactValue ?? '').trim().length > 0)
                            .map((item) => [String(item.contactValue ?? '').trim(), item])
                    )
                    const localUnsavedContacts = (deauthContacts.value || [])
                        .filter((item) => !item?.id && String(item?.contactValue ?? '').trim().length > 0)
                        .map((item) => ({
                            id: null,
                            contactType: item.contactType ?? null,
                            contactValue: String(item.contactValue ?? '').trim(),
                            displayName: item.displayName ?? null,
                            isVerified: item.isVerified ?? null,
                            isVerificationChecked: Boolean(item.isVerificationChecked),
                            pendingRemovalId: item.pendingRemovalId ?? null,
                        }))
                    // Локально измененные сохраненные контакты не должны "откатываться"
                    // после точечных операций (например, verify/save другого контакта).
                    const localModifiedSavedById = (deauthContacts.value || []).reduce((acc, item) => {
                        const id = String(item?.id ?? '').trim()
                        if (!id) return acc
                        const currentValue = String(item?.contactValue ?? '').trim()
                        const initialValue = String(initialDeauthContactsById.value?.[id] ?? '').trim()
                        if (!currentValue || currentValue === initialValue) return acc
                        acc[id] = {
                            ...item,
                            contactValue: currentValue,
                            // При локальном изменении старое имя/верификация невалидны до новой проверки.
                            displayName: null,
                            isVerified: null,
                            isVerificationChecked: false,
                        }
                        return acc
                    }, {})

                    const res = await api.deauthNotification.getSettings(projectId)
                    const errorPayload = res?.error || res?.data?.error
                    if (Number(errorPayload?.code) === 403) {
                        deauthAccessErrorMessage.value = String(errorPayload?.message || 'not enough permissions')
                        deauthContacts.value = []
                        sendToReportRecipients.value = false
                        return
                    }
                    deauthAccessErrorMessage.value = ''
                    const payload = res?.data?.data ?? res?.data ?? {}
                    const serverContacts = normalizeDeauthContacts(payload?.contacts).map((item) => {
                        const itemId = String(item?.id ?? '').trim()
                        const localModified = itemId ? localModifiedSavedById[itemId] : null
                        if (localModified) {
                            return {
                                ...localModified,
                                pendingRemovalId: localModified.pendingRemovalId ?? null,
                            }
                        }
                        const byId = item?.id ? previousById.get(String(item.id)) : null
                        const byValue = previousByValue.get(String(item?.contactValue ?? '').trim())
                        const prev = byId || byValue
                        return {
                            ...item,
                            isVerificationChecked: prev?.isVerificationChecked ?? false,
                            pendingRemovalId: prev?.pendingRemovalId ?? null,
                        }
                    })
                    const serverValues = new Set(
                        serverContacts.map((item) => String(item?.contactValue ?? '').trim())
                    )
                    const mergedUnsavedContacts = localUnsavedContacts.filter(
                        (item) => !serverValues.has(String(item?.contactValue ?? '').trim())
                    )
                    deauthContacts.value = mergeDeauthContactsPreservingOrder(
                        previousContacts,
                        serverContacts,
                        mergedUnsavedContacts,
                    )
                    if (deauthContacts.value.length === 0) {
                        deauthContacts.value = [
                            {
                                id: null,
                                contactType: null,
                                contactValue: '',
                                displayName: null,
                                isVerified: null,
                                isVerificationChecked: false,
                                pendingRemovalId: null,
                            },
                        ]
                    }
                    initialDeauthContactIds.value = deauthContacts.value
                        .map((item) => String(item?.id ?? ''))
                        .filter((id) => id.length > 0)
                    initialDeauthContactsById.value = deauthContacts.value.reduce((acc, item) => {
                        const id = String(item?.id ?? '').trim()
                        if (!id) return acc
                        acc[id] = String(item?.contactValue ?? '').trim()
                        return acc
                    }, {})
                    initialDeauthContactsMetaById.value = deauthContacts.value.reduce((acc, item) => {
                        const id = String(item?.id ?? '').trim()
                        if (!id) return acc
                        acc[id] = {
                            contactType: item?.contactType ?? null,
                            displayName: item?.displayName ?? null,
                            isVerified: typeof item?.isVerified === 'boolean' ? item.isVerified : null,
                        }
                        return acc
                    }, {})
                    sendToReportRecipients.value = Boolean(payload?.sendToReportRecipients)
                } catch (e) {
                    deauthAccessErrorMessage.value = ''
                    if (!props.onboardingMode) {
                        root.$store.dispatch('callNotify', 'Ошибка при загрузке настроек уведомлений о деавторизации')
                    }
                }
            }

            const fetchDeauthAllGroupsIfNeeded = async () => {
                if (!(props.displayMode === 'sending-settings' || props.displayMode === 'mailings' || props.displayMode === 'notifications' || props.displayMode === 'default')) return
                if (!isSuperUser.value) return
                try {
                    const res = await api.scheduledReport.getTgChats()
                    const list = Array.isArray(res?.data) ? res.data : []
                    deauthAllGroups.value = list
                        .filter((item) => item && item.id !== undefined && item.name !== undefined)
                        .map((item) => ({ id: String(item.id), name: String(item.name) }))
                } catch (e) {
                    deauthAllGroups.value = []
                }
            }

            const updateDeauthContactsValue = (newValues) => {
                const arr = Array.isArray(newValues) ? newValues : []
                const previous = deauthContacts.value || []
                const used = new Set()
                const normalize = (v) => String(v ?? '').trim()

                const findUnusedByValue = (trimmed) =>
                    previous.findIndex((c, idx) => !used.has(idx) && normalize(c?.contactValue) === trimmed)

                const findFirstUnused = () => previous.findIndex((_, idx) => !used.has(idx))

                const updated = arr.map((val, i) => {
                    const trimmed = normalize(val)

                    let prevIdx = -1
                    // Сначала привязка по индексу (важно при дубликатах значения в разных строках).
                    if (i < previous.length && !used.has(i)) {
                        const prevAtIndex = previous[i]
                        if (!trimmed || normalize(prevAtIndex?.contactValue) === trimmed) {
                            prevIdx = i
                        }
                    }
                    if (prevIdx === -1) {
                        const j = findUnusedByValue(trimmed)
                        if (j !== -1) {
                            prevIdx = j
                        } else {
                            const k = findFirstUnused()
                            if (k !== -1) {
                                prevIdx = k
                            }
                        }
                    }

                    if (prevIdx === -1) {
                        return {
                            id: null,
                            contactType: null,
                            contactValue: trimmed,
                            displayName: null,
                            isVerified: null,
                            isVerificationChecked: false,
                            pendingRemovalId: null,
                        }
                    }

                    used.add(prevIdx)
                    const prev = previous[prevIdx]

                    if (normalize(prev.contactValue) === trimmed) {
                        return prev
                    }

                    const prevId = String(prev?.id ?? '').trim()
                    const initialValueById = normalize(initialDeauthContactsById.value?.[prevId])
                    const initialMetaById = initialDeauthContactsMetaById.value?.[prevId] || {}
                    const isRevertedToInitialSavedValue =
                        prevId.length > 0 &&
                        trimmed.length > 0 &&
                        initialValueById.length > 0 &&
                        trimmed === initialValueById

                    if (isRevertedToInitialSavedValue) {
                        return {
                            ...prev,
                            contactValue: trimmed,
                            contactType: initialMetaById?.contactType ?? prev?.contactType ?? null,
                            displayName: initialMetaById?.displayName ?? null,
                            isVerified: typeof initialMetaById?.isVerified === 'boolean'
                                ? initialMetaById.isVerified
                                : null,
                            isVerificationChecked: false,
                            pendingRemovalId: null,
                            verificationRequestFailed: false,
                        }
                    }

                    return {
                        ...prev,
                        contactValue: trimmed,
                        displayName: null,
                        isVerified: null,
                        isVerificationChecked: false,
                        pendingRemovalId: null,
                        verificationRequestFailed: false,
                    }
                })
                deauthContacts.value = updated
            }

            const updateSendToReportRecipients = async (value) => {
                const newVal = Boolean(value)
                sendToReportRecipients.value = newVal
                const projectId = getDeauthProjectId()
                if (!projectId) return
                try {
                    await api.deauthNotification.switchSendToReportRecipients({ projectId, sendToReportRecipients: newVal })
                } catch (e) {
                    root.$store.dispatch('callNotify', 'Ошибка при обновлении настроек уведомлений')
                }
            }

            const fetchApp = async () => {
                if (!props.connector?.connectorId) return
                const hasApp = Boolean(props.connector.appCodeImmutable)
                // Как в старом whatsappBasis/Settings.vue: если приложения ещё нет (режим создания),
                // не вызываем getApp (он вернёт 500), а заполняем форму дефолтами из getDefaultWhatsappAppSettings
                if (!hasApp && botCrmConnector.value?.connectorId) {
                    try {
                        const salonId = Number(botCrmConnector.value?.companyIds?.[0] ?? 0)
                        settings.value = await getDefaultWhatsappAppSettings({
                            channel: {},
                            connectorId: props.connector.connectorId,
                            salonId: salonId || 0,
                            yClientsConnectorId: altegioConnector.value
                                ? undefined
                                : yClientsConnector.value?.connectorId,
                            altegioConnectorId: altegioConnector.value?.connectorId,
                        })
                        ensureRedirectDefaults()
                        settings.value.redirect.needToRedirect = true
                        if (props.initialDefaultChannelId) {
                            const normalizedChannelId = Number(props.initialDefaultChannelId)
                            const channelRef = {
                                type: 'channel',
                                id: Number.isNaN(normalizedChannelId) ? props.initialDefaultChannelId : normalizedChannelId,
                            }
                            settings.value.defaultChannel = channelRef
                            settings.value.yclientsChannel = channelRef
                        }
                        restorePreservedSettingsIfNeeded()
                        return
                    } catch (e) {
                        if (!props.onboardingMode) {
                            root.$store.dispatch('callNotify', 'Ошибка при загрузке настроек по умолчанию')
                        }
                        restorePreservedSettingsIfNeeded()
                        return
                    }
                }
                try {
                    const res = await api.networkFixer.getApp({ connectorId: props.connector.connectorId })
                    if (!res.error && res.data?.data) {
                        settings.value = res.data.data
                        // При онбординге и последующих открытиях подтягиваем defaultChannel/yclientsChannel
                        // из настроек коннектора, если fixer их не вернул.
                        try {
                            const getter = root?.$store?.getters?.connectorById
                            const connectorFromStore =
                                typeof getter === 'function'
                                    ? getter(props.connector.connectorId)
                                    : props.connector
                            const connectorSettings = connectorFromStore?.settings || {}
                            if (!settings.value.defaultChannel && connectorSettings.defaultChannel) {
                                settings.value.defaultChannel = connectorSettings.defaultChannel
                            }
                            if (!settings.value.yclientsChannel && connectorSettings.yclientsChannel) {
                                settings.value.yclientsChannel = connectorSettings.yclientsChannel
                            }
                        } catch (e) {
                            // мягко игнорируем проблемы с store, настройки приложения уже загружены
                        }
                        ensureRedirectDefaults()
                    }
                    restorePreservedSettingsIfNeeded()
                } catch (error) {
                    restorePreservedSettingsIfNeeded()
                    if (!props.onboardingMode) {
                        root.$store.dispatch('callNotify', 'Ошибка при загрузке настроек')
                    }
                }
            }

            // При онбординге YC коннектор whatsappBasis создаётся после маунта — ждём появления connectorId и только
            // после этого подтягиваем полные настройки приложения из networkFixer.
            watch(
                () => props.connector && props.connector.connectorId,
                async (id) => {
                    if (id) {
                        if (suppressConnectorFetchApp.value) {
                            return
                        }
                        await fetchApp()
                    }
                },
                { immediate: true }
            )

            const fetchDefaultMessageVarsIfNeeded = async () => {
                if (props.displayMode !== 'sending-settings' && props.displayMode !== 'mailings') return

                const projectId = String(
                    project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id || '',
                )
                if (!projectId) return

                try {
                    const varIds = Object.values(DEFAULT_MESSAGE_VAR_ID_BY_KEY)
                    const { data, error } = await api.gcb2.getMessageVarsValues({ projectId, varIds })
                    if (error) {
                        root.$store.dispatch('callNotify', 'Ошибка при загрузке переменных по умолчанию')
                        return
                    }

                    const defaults = {}
                    Object.keys(DEFAULT_MESSAGE_VAR_ID_BY_KEY).forEach((key) => {
                        const varId = DEFAULT_MESSAGE_VAR_ID_BY_KEY[key]
                        const row =
                            (data || []).find((r) => r && r.varId === varId && (r.isDefault || r.id === '_DEFAULT_MESSAGE_VAR' || r.id === null)) ||
                            null
                        defaults[key] = row?.value ?? ''
                    })

                    defaultMessageVarsInit.value = Object.assign({}, defaults)
                    // Не перетираем значения из настроек бота, если они уже заданы пользователем/CRM
                    const merged = Object.assign({}, settings.value ?? {})
                    Object.keys(DEFAULT_MESSAGE_VAR_ID_BY_KEY).forEach((key) => {
                        const cur = get(merged, key)
                        if (isNil(cur) || String(cur).trim() === '') {
                            merged[key] = defaults[key]
                        }
                    })
                    settings.value = merged
                } catch (e) {
                    root.$store.dispatch('callNotify', 'Ошибка при загрузке переменных по умолчанию')
                }
            }

            const fetchDailyLimitIfNeeded = async () => {
                if (props.displayMode !== 'sending-settings' && props.displayMode !== 'mailings') return
                if (!hasMessagingConnector.value) return

                const projectId = String(
                    project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id || '',
                )
                if (!projectId) return

                try {
                    const { data, error } = await api.massender.getLimitSettings({
                        projectId,
                        connectorType: messagingConnector.value.connectorType,
                    })
                    if (error) {
                        root.$store.dispatch('callNotify', 'Ошибка при загрузке лимита пакетной отправки')
                        return
                    }
                    massenderLimitSettingsInit.value = data || null
                    const limit = get(data, 'default_daily_limit_of_messages_items')
                    const current = get(settings.value, 'dailyLimit')
                    if (!isNil(limit) && (isNil(current) || String(current).trim() === '')) {
                        updateSetting('dailyLimit', String(limit))
                    }
                } catch (e) {
                    root.$store.dispatch('callNotify', 'Ошибка при загрузке лимита пакетной отправки')
                }
            }

            // Загрузка каскадов в зависимости от режима.
            // При онбординге (YC/DD и т.д.) каскады ещё не созданы — запрос к BFF даёт 500, не делаем его.
            const fetchCascade = async () => {
                const isOnboarding =
                    (root.$router.currentRoute.path || '').includes('onBoarding') ||
                    Boolean(project.value?.isOnBoardingActive)
                if (isOnboarding) {
                    cascades.value = []
                    return
                }

                const allowedCascadeTypes =
                    props.displayMode === 'notifications'
                        ? ['notifications', 'custom', 'crmMessages']
                        : props.displayMode === 'mailings'
                        ? ['mailings', 'crmMessages']
                        : props.displayMode === 'sending-settings'
                            ? ['notifications', 'custom', 'mailings', 'crmMessages']
                            : ['crmMessages']
                if (!allowedCascadeTypes.length) return

                const projectId = project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id
                if (!projectId) {
                    return
                }

                try {
                    const { data: projectCascades, error } = await getCascadesByProject(projectId)
                    if (!error && projectCascades) {
                        cascades.value = projectCascades.filter(c => allowedCascadeTypes.includes(c.cascadeType))
                    } else {
                        cascades.value = []
                    }
                } catch (error) {
                    console.error('Ошибка загрузки каскада:', String(error))
                    cascades.value = []
                }
            }

            onMounted(async () => {
                loading.value = true
                await fetchApp()
                await fetchDefaultMessageVarsIfNeeded()
                await fetchDailyLimitIfNeeded()
                await fetchCascade()
                await fetchDeauthSettingsIfNeeded()
                await fetchDeauthAllGroupsIfNeeded()
                if (hasBotConnector.value) {
                if (isString(settings.value.claim?.claimPhone)) {
                    updateSetting('claim.claimPhone', [settings.value.claim.claimPhone])
                }
                if (isUndefined(settings.value.cancel?.cancelPhone)) {
                    updateSetting('cancel.cancelPhone', [])
                }
                // Инициализируем defaultChannel, если его нет
                if (!settings.value.defaultChannel) {
                    updateSetting('defaultChannel', null)
                    }
                }
                // Устанавливаем по умолчанию открытыми панели:
                // - notifications/default: открыты первые три (0, 1 и 2)
                // - mailings: открыта первая (0)
                if (props.displayMode === 'notifications' || props.displayMode === 'default') {
                    stepsIndexes.value = [0, 1, 2]
                } else if (props.displayMode === 'mailings') {
                    stepsIndexes.value = [0]
                }
                loading.value = false
            })

            return {
                loading,
                saveButtonLoading,
                settings,
                deauthContacts,
                deauthContactsValue,
                deauthAllGroups,
                deauthAccessErrorMessage,
                deauthVerifyingIndices,
                sendToReportRecipients,
                redirectMode,
                hasBotConnector,
                isSuperUser,
                canManageDeauthRecipients,
                isMailingsCascadeEnabled,
                sendingStepsIndexes,
                stepsIndexes,
                isValid,
                mainSettingsValid,
                informationSettingsValid,
                saveButtonText,
                hasPendingDeauthChanges,
                modeHintText,
                redirectPhoneHintText,
                visitGroupHintText,
                informationSettingsHintText,
                updateSetting,
                setTimezone,
                save,
                updateDeauthContactsValue,
                updateSendToReportRecipients,
                fetchDeauthSettingsIfNeeded,
                project,
                initialDeauthContactIds,
                initialDeauthContactsById,
                initialDeauthContactsMetaById,
                get,
                phoneValidator,
                isValidPhone,
                yClientsConnector,
                altegioConnector,
                botCrmConnector,
                gcb2Connector,
                hasMessagingConnector,
                channelOptions,
                mailingsChannelOptions,
                yclientsChannelOptions,
                selectedChannel,
                updateChannel,
                effectiveActivationSendChannel,
                selectedActivationSendChannel,
                updateActivationSendChannel,
                selectedYclientsChannel,
                updateYclientsChannel,
                extraVarFields,
                displayMode: props.displayMode,
                requiredFields,
            }
        },
        methods: {
            normalizeDeauthContactValue(value) {
                return String(value ?? '').trim()
            },
            async removeDeauthContact(index) {
                const contact = this.deauthContacts[index]
                if (!contact) return

                // Удаляем именно строку по индексу; иначе при одинаковых значениях «съезжает» другая строка.
                this.deauthContacts = this.deauthContacts.filter((_, i) => i !== index)

                const contactId = String(contact.id ?? '')
                const pendingRemovalId = String(contact.pendingRemovalId ?? '')
                const candidateId = contactId || pendingRemovalId
                const shouldRemoveOnBackend =
                    candidateId &&
                    (this.initialDeauthContactIds.includes(candidateId) || Boolean(pendingRemovalId))
                if (!shouldRemoveOnBackend) return

                const projectId = String(
                    this.project?.projectId || this.project?.id || this.$router.currentRoute.params.id || ''
                )
                if (!projectId) return

                try {
                    await api.deauthNotification.removeContact({ projectId, id: candidateId })
                    this.initialDeauthContactIds = this.initialDeauthContactIds.filter((id) => id !== candidateId)
                    await this.fetchDeauthSettingsIfNeeded()
                } catch (e) {
                    this.$store.dispatch('callNotify', 'Ошибка при удалении контакта')
                }
            },
            async verifyDeauthContact(index) {
                let contact = this.deauthContacts[index]
                if (!contact) return
                const projectId = String(
                    this.project?.projectId || this.project?.id || this.$router.currentRoute.params.id || ''
                )
                if (!projectId) return
                const normalizedCurrentValue = this.normalizeDeauthContactValue(contact.contactValue)
                const hasDuplicate = this.deauthContacts.some((item, i) =>
                    i !== index &&
                    this.normalizeDeauthContactValue(item?.contactValue) === normalizedCurrentValue &&
                    normalizedCurrentValue.length > 0
                )
                if (hasDuplicate && !contact.id) {
                    this.$store.dispatch('callNotify', 'Такое значение уже добавлено')
                    return
                }

                const prev = Array.isArray(this.deauthVerifyingIndices) ? this.deauthVerifyingIndices : []
                this.deauthVerifyingIndices = prev.includes(index) ? prev : [...prev, index]
                try {
                    this.deauthContacts = this.deauthContacts.map((c, i) =>
                        i === index ? { ...c, verificationRequestFailed: false } : c
                    )
                    contact = this.deauthContacts[index]
                    const existingId = String(contact.id ?? '').trim()
                    const normalizedValue = String(contact.contactValue ?? '').trim()
                    if (existingId) {
                        const initialValue = String(this.initialDeauthContactsById?.[existingId] ?? '').trim()
                        if (normalizedValue && normalizedValue !== initialValue) {
                            const updateRes = await api.deauthNotification.updateContact({
                                projectId,
                                id: existingId,
                                contactValue: normalizedValue,
                            })
                            const updated = updateRes?.data?.data ?? updateRes?.data ?? {}
                            this.deauthContacts = this.deauthContacts.map((c, i) =>
                                i === index
                                    ? {
                                        ...c,
                                        contactType: updated.contactType ?? c.contactType ?? null,
                                        contactValue: updated.contactValue ?? c.contactValue,
                                        displayName: updated.displayName ?? null,
                                        isVerified: typeof updated?.isVerified === 'boolean'
                                            ? updated.isVerified
                                            : (typeof updated?.verified === 'boolean' ? updated.verified : null),
                                        isVerificationChecked: false,
                                        pendingRemovalId: null,
                                    }
                                    : c
                            )
                            this.initialDeauthContactsById = {
                                ...(this.initialDeauthContactsById || {}),
                                [existingId]: String(updated.contactValue ?? normalizedValue).trim(),
                            }
                            this.initialDeauthContactsMetaById = {
                                ...(this.initialDeauthContactsMetaById || {}),
                                [existingId]: {
                                    contactType: updated.contactType ?? contact.contactType ?? null,
                                    displayName: updated.displayName ?? null,
                                    isVerified: typeof updated?.isVerified === 'boolean'
                                        ? updated.isVerified
                                        : (typeof updated?.verified === 'boolean' ? updated.verified : null),
                                },
                            }
                            contact = this.deauthContacts[index]
                        }
                    }

                    if (!contact.id) {
                        const saveRes = await api.deauthNotification.updateContacts({
                            projectId,
                            contactValue: normalizedValue,
                        })
                        const saved = saveRes?.data?.data ?? saveRes?.data ?? {}
                        const savedId = String(saved.id ?? '')

                        this.deauthContacts = this.deauthContacts.map((c, i) =>
                            i === index
                                ? {
                                    ...c,
                                    id: savedId || null,
                                    contactType: saved.contactType ?? c.contactType ?? null,
                                    contactValue: saved.contactValue ?? c.contactValue,
                                    displayName: saved.displayName ?? null,
                                    isVerified: typeof saved?.isVerified === 'boolean'
                                        ? saved.isVerified
                                        : (typeof saved?.verified === 'boolean' ? saved.verified : null),
                                    isVerificationChecked: false,
                                    pendingRemovalId: null,
                                }
                                : c
                        )
                        await this.fetchDeauthSettingsIfNeeded()
                        contact = this.deauthContacts.find((c) => String(c?.id ?? '') === savedId) || this.deauthContacts[index]
                    }

                    const contactAfterSave = contact?.id
                        ? contact
                        : this.deauthContacts[index]
                    if (!contactAfterSave?.id) return
                    const targetId = String(contactAfterSave.id)
                    const verifyRes = await api.deauthNotification.verifyContacts({
                        projectId,
                        contactId: targetId,
                    })
                    const actualIndex = this.deauthContacts.findIndex((c) => String(c?.id ?? '') === targetId)
                    const applyIndex = actualIndex !== -1 ? actualIndex : index
                    if (verifyRes?.error != null) {
                        this.$store.dispatch('callNotify', 'Ошибка при проверке контакта')
                        this.deauthContacts = this.deauthContacts.map((c, i) =>
                            i === applyIndex
                                ? {
                                    ...c,
                                    verificationRequestFailed: true,
                                    isVerificationChecked: false,
                                }
                                : c
                        )
                        return
                    }
                    const verified = verifyRes?.data?.data ?? verifyRes?.data ?? {}
                    this.deauthContacts = this.deauthContacts.map((c, i) =>
                        i === applyIndex
                            ? {
                                ...c,
                                displayName: Object.prototype.hasOwnProperty.call(verified, 'displayName')
                                    ? (verified.displayName ?? null)
                                    : (c.displayName ?? null),
                                isVerified: typeof verified?.isVerified === 'boolean'
                                    ? verified.isVerified
                                    : (typeof verified?.verified === 'boolean' ? verified.verified : false),
                                isVerificationChecked: true,
                                verificationRequestFailed: false,
                            }
                            : c
                    )
                } catch (e) {
                    this.$store.dispatch('callNotify', 'Ошибка при проверке контакта')
                    this.deauthContacts = this.deauthContacts.map((c, i) =>
                        i === index
                            ? {
                                ...c,
                                verificationRequestFailed: true,
                                isVerificationChecked: false,
                            }
                            : c
                    )
                } finally {
                    const cur = Array.isArray(this.deauthVerifyingIndices) ? this.deauthVerifyingIndices : []
                    this.deauthVerifyingIndices = cur.filter((i) => i !== index)
                }
            },
        },
    })
</script>

<style lang="sass" scoped>
    @import '@/vars.sass'

    .notification-settings
        color: #1F2440

    .settings-panels
        background: transparent !important

        .v-expansion-panel
            background: #fff
            border: 1px solid #E6E9F0
            border-radius: 16px !important
            margin-bottom: 0
            box-shadow: 0 1px 2px rgba(26, 26, 46, .03)

            &::before
                box-shadow: none !important

    .panel-header
        padding: 22px 24px
        min-height: auto !important

        .panel-header-content
            display: flex
            flex-direction: column
            gap: 8px

    .panel-title
        font-family: Geologica, sans-serif
        font-size: 24px
        font-weight: 900
        letter-spacing: -.03em
        color: #1A1A2E

    .panel-section-title
        font-family: Geologica, sans-serif
        font-size: 17px
        font-weight: 900
        letter-spacing: -.01em
        color: #1A1A2E
        line-height: 1.35

    .panel-section-title__mobile
        display: none

    @media (max-width: 959px)
        .panel-section-title--deauth
            .panel-section-title__desktop
                display: none

            .panel-section-title__mobile
                display: inline

    .subtitle
        font-size: 13px
        color: #1F2440

    .panel-content
        display: flex
        flex-direction: column
        gap: 14px

        > div
            display: flex
            flex-direction: column
            gap: 9px

        .field-label
            display: flex
            align-items: center
            gap: 7px
            margin: 0

    // ── Поля нового дизайна (deep: стили должны доходить до дочерних блоков) ──
    // Рамку рисуем на .v-input__slot, fieldset Vuetify скрываем —
    // иначе контент и рамка живут в разных боксах и «вылезают»/съезжают по вертикали
    ::v-deep .phone-input,
    ::v-deep .timezone-picker,
    ::v-deep .custom-input,
    ::v-deep .mock-input,
    ::v-deep .select-input,
    ::v-deep .phone-group-input .validationInput,
    ::v-deep .phone-group-input .base-input-outlined-wrapper
        width: 100%

        .v-input
            width: 100%

        .validationInput
            width: 100%

    ::v-deep .v-text-field--outlined
        .v-input__control
            min-height: 48px !important

        .v-input__slot
            display: flex !important
            align-items: center !important
            min-height: 48px !important
            height: 48px !important
            margin: 0 !important
            padding: 0 12px !important
            background: #fff !important
            border: 1px solid #E6E9F0 !important
            border-radius: 11px !important
            box-shadow: none !important
            overflow: hidden

        .v-text-field__slot
            display: flex !important
            align-items: center !important
            margin: 0 !important
            height: 100% !important
            min-height: 0 !important

        // прячем нативную рамку Vuetify
        fieldset
            border: none !important
            margin: 0 !important
            padding: 0 !important
            top: 0 !important
            bottom: 0 !important
            left: 0 !important
            right: 0 !important
            height: 100% !important
            pointer-events: none

            legend
                display: none !important
                width: 0 !important
                height: 0 !important
                padding: 0 !important

        input,
        .v-select__selection
            margin-top: 0 !important
            margin-bottom: 0 !important
            padding-top: 0 !important
            padding-bottom: 0 !important
            align-self: center !important
            line-height: 22px !important
            max-height: none !important
            height: auto !important
            color: #1A1A2E !important
            font-family: Inter, Geologica, system-ui, sans-serif !important
            font-size: 14px !important

        input::placeholder,
        .v-label
            color: #AAB2C4 !important
            font-size: 14px !important
            opacity: 1 !important

        .v-label
            top: 50% !important
            transform: translateY(-50%) !important
            margin: 0 !important
            left: 0 !important
            right: auto !important
            position: absolute !important
            max-width: calc(100% - 8px) !important
            overflow: hidden
            text-overflow: ellipsis
            white-space: nowrap
            pointer-events: none

        .v-label--active
            display: none !important

        .v-input__append-inner,
        .v-input__prepend-inner
            margin-top: 0 !important
            margin-bottom: 0 !important
            align-self: center !important
            padding: 0 !important
            height: auto !important
            display: flex !important
            align-items: center !important

        .v-input__icon
            height: 24px !important
            min-width: 24px !important
            width: 24px !important
            align-items: center !important

        .v-input__icon .v-icon
            font-size: 20px !important

        &.v-input--is-focused .v-input__slot,
        &.v-input--is-focused.v-text-field--outlined .v-input__slot
            border-color: #525AD5 !important
            box-shadow: 0 0 0 3px rgba(82, 90, 213, .12) !important

    // Ошибки ValidationInput — не только в panel-content, но и в дочерних блоках
    ::v-deep .settings-validation-item
        margin: 0
        padding-bottom: 0 !important

        .warn
            position: static
            display: block
            margin-top: 6px
            bottom: auto
            line-height: 1.3

        .v-input
            margin-bottom: 0 !important
            padding-bottom: 0 !important

        .v-text-field
            margin-top: 0 !important
            padding-top: 0 !important

        .v-input__details,
        .v-messages
            min-height: 0
            margin: 0

    // PhoneInputGroup (отзывы/отмены) — НЕ трогаем PhoneOrTgInputGroup.starter-pack
    ::v-deep .phone-group-input:not(.text-input-group--starter-pack)
        .text-input-group__item
            display: flex
            align-items: center
            gap: 8px
            margin-bottom: 10px

        .v-input__slot
            min-height: 48px !important
            height: 48px !important
            border: 1px solid #E6E9F0 !important
            border-radius: 11px !important

        fieldset
            border: none !important

        .add-item-btn,
        .add-item-btn_icon
            border-radius: 9px !important
            border-color: #E6E9F0 !important
            font-family: Geologica, Inter, system-ui, sans-serif !important
            font-weight: 800 !important
            letter-spacing: .03em
            text-transform: uppercase !important

        .add-item-btn
            min-height: 40px !important
            padding: 0 16px !important

    ::v-deep .v-radio-group
        margin-top: 0 !important
        padding-top: 0 !important

    ::v-deep .v-radio
        .v-label
            color: #1F2440 !important
            font-size: 13px !important

    .unsaved-changes-popup
        position: fixed
        left: 16px
        bottom: 16px
        z-index: 20
        display: inline-flex
        align-items: center
        gap: 8px
        padding: 10px 14px
        max-width: 420px
        border-radius: 12px
        background: $dark-new
        box-shadow: 0 12px 30px rgba(26, 26, 46, .3)
        span
            color: #fff
            font-family: Geologica, sans-serif
            font-size: 13px
            font-weight: 700
            line-height: 20px

    .unsaved-changes-popup__save-btn
        background: #fff !important
        color: $main-new !important
        border-color: #fff !important
        font-family: Geologica, sans-serif
        font-size: 11px
        font-weight: 800
        letter-spacing: .04em
        text-transform: uppercase

    @media (max-width: 959px)
        .unsaved-changes-popup
            display: none !important

    .whatsapp-settings-form
        display: flex
        flex-direction: column
        flex-wrap: nowrap
        gap: 24px
        align-items: stretch
        width: 100%

        .button-panel
            align-self: flex-end

    .header-warning-text
        font: normal normal 500 12px/16px Inter, Geologica, sans-serif
        color: #7A839A

    .optional-text
        font-size: 11px
        color: #AAB2C4

    .warning-box,
    .note-warn
        background: #FFF7EA
        border: 1px solid #F6D69A
        border-radius: 11px
        padding: 13px 16px
        font-size: 12.5px
        line-height: 1.5
        color: #7A5A12

        strong,
        b
            color: #A8431A

    .btn-save
        background: #525AD5 !important
        color: #fff !important
        border: none !important
        border-radius: 11px !important
        font-family: Geologica, sans-serif !important
        font-weight: 800 !important
        font-size: 12px !important
        letter-spacing: .04em
        text-transform: uppercase !important
        padding: 13px 26px !important
        min-height: 46px !important
        height: auto !important
        box-shadow: 0 3px 14px rgba(82, 90, 213, .28) !important
        transition: background .15s, transform .15s

        &:hover:not(.v-btn--disabled)
            background: #454CC0 !important
            transform: translateY(-1px)

        &.v-btn--disabled
            opacity: .55 !important
            box-shadow: none !important

    ::v-deep .v-expansion-panels
        gap: 20px !important

    ::v-deep .v-expansion-panel--active,
    .v-expansion-panel
        margin-bottom: 0 !important
        margin-top: 0 !important

    ::v-deep .v-expansion-panel-header
        padding: 22px 24px !important
        min-height: auto !important

        .v-expansion-panel-header__icon
            margin-left: 12px !important

            .v-icon
                color: #AAB2C4 !important

    ::v-deep .v-expansion-panel-content__wrap
        padding: 0 24px 22px !important

    .sending-settings-mode,
    .mailings-mode,
    .notifications-mode
        .sending-settings-container
            display: flex
            flex-direction: column
            gap: 24px

        .save-button-container
            display: flex
            justify-content: flex-end
            margin-top: 24px

    ::v-deep .left-panel
        width: 100%
</style>
